import { EzLogger } from '/public/javascript/common/ezclocker-logger.js';

import {
    EzBadParamException,
    EzBadStateException,
    EzException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzChar,
    EzString,
    EzArray,
    EzFunction,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzTimeZone } from '/ezlibrary/date-time/EzTimeZone.js';
import { EzEmployerOption } from '/ezlibrary/EzEmployerOption.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzContextEventName } from '/ezlibrary/EzClockerContext/EzContextEventName.js';
import { EzLocale } from '/ezlibrary/locales/EzLocale.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';

/**
 * @class
 * @augments {EzClass}
 * @description
 * Represents a DateTime for ezclocker
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzDateTime, ezDateTime } from '/public/javascript/common/ez-date-time.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     EzClass.ezWaitReady(
 *         EzDateTime.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzDateTime extends EzClass {
    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default active timezone value of: 'AUTOMATIC'
     * @returns {string}
     * The default active time zone value ('AUTOMATIC'), meaning the time zone is detected from the browser.
     */
    static get DEFAULT_ACTIVE_TIMEZONE() {
        return 'AUTOMATIC';
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Date Formatting Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default moment.js ISO date time format string (does not include seconds or milliseconds): 'YYYY-MM-DD'
     * @returns {string}
     * The moment.js ISO 8601 date format string ('YYYY-MM-DD'), e.g. 1986-09-04.
     */
    static get DEFAULT_ISO_DATE_FORMAT() {
        return 'YYYY-MM-DD';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the moment.js format string: 'YYYY-MM-DD\'T\'HH:mm:ssZ'
     * @returns {string}
     * The moment.js ISO 8601 date time format string with minutes, seconds and zone offset ('YYYY-MM-DD\'T\'HH:mm:ssZ').
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_MIN_SEC_ZONE_FORMAT() {
        return 'YYYY-MM-DD\'T\'HH:mm:ssZ';
    }


    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default moment.js ISO date time format string (does not include seconds or milliseconds): 'YYYY-MM-DDTHH:mmZ'
     * @returns {string}
     * The moment.js ISO 8601 date time format string without seconds ('YYYY-MM-DDTHH:mmZ'), e.g. 1986-09-04T20:30-05:00.
     */
    static get DEFAULT_ISO_DATE_TIME_FORMAT() {
        return 'YYYY-MM-DDTHH:mmZ';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets date format string: 'MM/DD/YYYY'
     * @returns {string}
     * The moment.js date format string 'MM/DD/YYYY', e.g. 09/04/1986.
     */
    static get DTFORMAT_MM_DD_YYYY() {
        return 'MM/DD/YYYY';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets date format string: 'ddd MM/DD/YYYY'
     * @returns {string}
     * The moment.js day name and date format string 'ddd MM/DD/YYYY', e.g. Thu 09/04/1986.
     */
    static get DTFORMAT_ddd_MM_DD_YYYY() {
        return 'ddd MM/DD/YYYY';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets time format string: 'hh:mm a'
     * @returns {string}
     * The moment.js 12-hour time format string 'hh:mm a', e.g. 08:30 pm.
     */
    static get DTFORMAT_hh_mm_a() {
        return 'hh:mm a';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default moment.js ISO date time format string that includes seconds as: 'YYYY-MM-DDTHH:mm:ssZ'
     * @returns {string}
     * The moment.js ISO 8601 date time format string with seconds ('YYYY-MM-DDTHH:mm:ssZ'), e.g. 1986-09-04T20:30:25-05:00.
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_SECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ssZ';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default moment.js ISO date time format string that includes milliseconds as: 'YYYY-MM-DDTHH:mm:ss.999Z'
     * @returns {string}
     * The moment.js ISO 8601 date time format string with milliseconds ('YYYY-MM-DDTHH:mm:ss.999Z').
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_MILLISECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ss.999Z';
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Evaluates the provided timeZoneId to try and determine if it is a valid supported time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     * True if the provied timeZoneId is considered a valid and supported time zone id. False otherwise.
     */
    static isValidTimeZone(timeZoneId = null) {
        if (!EzString.hasText(timeZoneId)) {
            return false;
        }

        // See if we can parse the timeZoneId into a time zone instance
        try {
            const timeZoneInstance = moment.tz.zone(timeZoneId);

            timeZoneId = EzObject.isValid(timeZoneInstance) && timeZoneId?.toUpperCase() === timeZoneInstance?.name.toUpperCase()
                ? timeZoneInstance.name
                : null;

            if (!EzString.hasText(timeZoneId)) {
                return false;
            }

            // Verify the time zone instance's time zone id is supported by moment:
            return EzObject.isValid(moment().tz(timeZoneId));
        } catch (err) {
            if (!EzString.hasText(err?.message)) {
                err.message = `Unexpected error while getting now for timeZoneId=${timeZoneId}`;
            }

            const em = EzString.em`
                The provided timeZoneId of "${timeZoneId}" is either not a valid time zone id or the time zone id is currently not supported.
                [Error reported: ${err.message}]`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em, err);
            } else {
                globalThis?.console?.error(em, err);
            }
        }

        return false;
    }


    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided timeZoneId is considered a valid date time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidTimeZone(timeZoneId)
     */
    static ezIsValidTimeZone = EzDateTime.isValidTimeZone;

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: 8:30 PM
     * @returns {string}
     * The moment.js localized short time format token ('LT'), e.g. 8:30 PM.
     */
    static get SHORT_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: 8:30:25 PM
     * @returns {string}
     * The moment.js localized short time with seconds format token ('LTS'), e.g. 8:30:25 PM.
     */
    static get SHORT_TIME_WITH_SECONDS_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: 09/04/1986
     * @returns {string}
     * The moment.js localized two-digit short date format token ('L'), e.g. 09/04/1986.
     */
    static get TWO_DIGIT_SHORT_DATE() {
        return EzChar.L;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: 9/4/1986
     * @returns {string}
     * The moment.js localized short date format token ('l'), e.g. 9/4/1986.
     */
    static get SHORT_DATE() {
        return EzChar.l;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: September 4, 1986
     * @returns {string}
     * The moment.js localized long month date format token ('LL'), e.g. September 4, 1986.
     */
    static get LONG_MONTH_DATE_FORMAT() {
        return 'LL';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: Sep 4, 1986
     * @returns {string}
     * The moment.js localized short month date format token ('ll'), e.g. Sep 4, 1986.
     */
    static get SHORT_MONTH_DATE_FORMAT() {
        return 'll';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: September 4, 1986 8:30 PM
     * @returns {string}
     * The moment.js localized long month date and short time format token ('LLL'), e.g. September 4, 1986 8:30 PM.
     */
    static get LONG_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'LLL';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: Sep 4, 1986 8:30 PM
     * @returns {string}
     * The moment.js localized short month date and short time format token ('lll'), e.g. Sep 4, 1986 8:30 PM.
     */
    static get SHORT_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'lll';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: Thursday, September 4, 1986 8:30 PM
     * @returns {string}
     * The moment.js localized day name, long month date and short time format token ('LLLL'), e.g. Thursday, September 4, 1986 8:30 PM.
     */
    static LONG_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'LLLL';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns format string for: Thu, Sep 4, 1986 8:30 PM
     * @returns {string}
     * The moment.js localized short day name, short month date and short time format token ('llll'), e.g. Thu, Sep 4, 1986 8:30 PM.
     */
    static SHORT_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'llll';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the default display date and time format string for moment.js
     * Format string: 'L LT'
     * Returns in localized format: 09/04/1986 8:30 PM
     * @returns {string}
     * The moment.js default display date and time format string ('L LT'), e.g. 09/04/1986 8:30 PM.
     */
    static get DEFAULT_DISPLAY_DATE_TIME() {
        return 'L LT';
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns the default display date and time with seconds format string for moment.js
     * Format string: 'L LTS'
     * Returns in localized format: 09/04/1986 8:30:25 PM
     * @returns {string}
     * The moment.js default display date and time with seconds format string ('L LTS'), e.g. 09/04/1986 8:30:25 PM.
     */
    static get DEFAULT_DISPLAY_DATE_TIME_WITH_SECONDS() {
        return 'L LTS';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the default display date format string for moment.js
     * Format string: EzChar.L
     * Returns in localized format: 09/04/1986
     * @returns {string}
     * The moment.js default display date format token ('L'), e.g. 09/04/1986.
     */
    static get DEFAULT_MOMENT_DATE_FORMAT() {
        return EzChar.L;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default momentjs time-stanp display format string.
     * @returns {string}
     * The moment.js default timestamp display format string ('MM/DD/YYYY hh:mm:ss'), e.g. 09/04/1986 08:30:25.
     */
    static get DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT() {
        return 'MM/DD/YYYY hh:mm:ss';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the format string for displaying the date as: MM/YYYY
     * @returns {string}
     * The moment.js month and year display format string ('MM/YYYY'), e.g. 09/1986.
     */
    static get DEFAULT_MONTH_YEAR_DISPLAY_FORMAT() {
        return 'MM/YYYY';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Default hour and minuit display time format
     * @returns {string}
     * The moment.js default hour and minute display format string ('hh:mm a'), e.g. 08:30 pm.
     */
    static get DEFAULT_HH_MM_DISPLAYFORMAT() {
        return 'hh:mm a';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the default display day and date format string for moment.js Format string: 'dddd L'
     * Returns in localized format: Thursday 09/04/1986
     * @returns {string}
     * The moment.js default short day name and date display format string ('ddd L'), e.g. Thu 09/04/1986.
     */
    static get DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT() {
        return 'ddd L';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the default time display without seconds format string for moment.js Format string: 'LT'
     * Returns in localized format: 8:30 PM
     * @returns {string}
     * The moment.js localized hour and minute time format token ('LT'), e.g. 8:30 PM.
     */
    static get DEFAULT_MOMENT_HH_MM_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default time display format string for moment.js
     * Format string: 'LTS'
     * Result is in localized format: 8:30:25 PM
     * @returns {string}
     * The moment.js localized hour, minute and second time format token ('LTS'), e.g. 8:30:25 PM.
     */
    static get DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the REGEX value for validation ISO dates.
     * @returns {RegExp}
     * The regular expression used to validate ISO 8601 date and date time strings.
     */
    static get ISO_VALIDATION_REGEX() {
        // Possible Alternative RegEx: /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/
        // eslint-disable-next-line no-useless-escape
        return /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets date format string: 'MM/DD/YYYY'
     * @returns {string}
     * The date format string 'MM/DD/YYYY', e.g. 09/04/1986.
     */
    static get DEFAULT_MM_DD_YYYY_FORMAT() {
        return 'MM/DD/YYYY';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default date picker display format string.
     * @returns {string}
     * The jQuery UI date picker format string ('mm/dd/yy'), e.g. 09/04/1986.
     */
    static get DEFAULT_DATE_PICKER_FORMAT() {
        return 'mm/dd/yy';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default date and time display format string for moment.js.
     * @returns {string}
     * The moment.js date and time display format built from the default date and time formats ('L[ at ]LT'), e.g. 09/04/1986 at 8:30 PM.
     */
    static get DEFAULT_MOMENT_DATE_TIME_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default date and time display format (with seconds) string for moment.js.
     * @returns {string}
     * The moment.js date and time with seconds display format ('L[ at ]LTS'), e.g. 09/04/1986 at 8:30:25 PM.
     */
    static get DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default date picker display format string.
     * @returns {string}
     * The default jQuery UI date picker format string (same as EzDateTime.DEFAULT_DATE_PICKER_FORMAT: 'mm/dd/yy').
     */
    static get DEFAULT_MONTH_DAY_YEAR_FORMAT() {
        return EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    static #timeZoneIds = {
        // Universal Time Zone
        UTC: 'UTC',
        // Hawaiian Standard Time
        HST: 'HST',
        // US Hawaiian Time (Honolulu)'
        'Pacific_Honolulu': 'Pacific/Honolulu',
        // Alaskan Standard Time
        AKDT: 'AKDT',
        // 'US Alaskan Time (Anchorage)
        'America_Anchorage': 'America/Anchorage',
        // Pacific Daylight Time
        PDT: 'PDT',
        // 'US Pacific Time (Los Ange
        // Pacific Standard Time
        PST: 'PST',
        'America_Los_Angeles': 'America/Los_Angeles',
        // US Mountain Standard Time
        UMST: 'UMST',
        // Mountain Standard Time
        MDT: 'MDT',
        // 'US Mountain Time (Denver)'),
        'America_Denver': 'America/Denver',
        // 'US Mountain Standard Time (Ph
        'America_Phoenix': 'America/Phoenix',
        // Canada Central Standard Time
        CCST: 'CCST',
        // Central Standard Time
        CDT: 'CDT',
        // 'US Central Time (Chicago)'),
        'America_Chicago': 'America/Chicago',
        // Eastern Daylight Time
        EDT: 'EDT',
        // 'US Eastern Time (New York)')
        // US Eastern Standard Time
        UEDT: 'UEDT',
        'America_New_York': 'America/New_York',
        // West Pacific Standard Time
        WPST: 'WPST',
        // 'Chamorro Standard Time (Guam)')
        'Pacific_Guam': 'Pacific/Guam'
    };

    static get timeZoneIds() {
        return this.#timeZoneIds;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a Time zone info instance from the provided time zone id.
     * @param {string} timeZoneId
     * The IANA time zone id to build the time zone info for, e.g. 'America/Chicago'.
     * @param {undefined|null|string} displayNameOverride
     * Optional display name to use for the info's value instead of the full time zone id, e.g. 'US Central Time (Chicago)'.
     * Default: null
     * @param {boolean} isAutomatic
     * True to build the 'Automatic (...)' entry that includes EzDateTime.DEFAULT_ACTIVE_TIMEZONE in its utc array; otherwise, false.
     * Default: false
     * @returns {object}
     * Example: {
     *      value: `Automatic (${fullZoneId})`,
     *      text: fullZoneId,
     *      zoneId: {
     *          full: fullZoneId,
     *          abbr: abbrZoneId
     *      },
     *      abbr: abbrZoneId,
     *      offset: zoneOffset,
     *      isdst: isDst,
     *      isAutomatic: true,
     *      utc: [
     *          EzDateTime.DEFAULT_ACTIVE_TIMEZONE,
     *          fullZoneId,
     *          abbrZoneId
     *      ]
     *  };
     */
    static buildTimeZoneInfoFromTimeZoneId(timeZoneId, displayNameOverride = null, isAutomatic = false) {
        if (!EzString.hasText(timeZoneId)) {
            throw new EzBadParamException(
                { timeZoneId },
                EzDateTime,
                EzDateTime.buildTimeZoneInfoFromTimeZoneId);
        }

        if (!EzBoolean.isBoolean(isAutomatic)) {
            isAutomatic = false;
        }

        const momentTimeZoneInfo = moment.tz.zone(timeZoneId);

        if (!EzObject.isValid(momentTimeZoneInfo)) {
            throw new EzBadParamException(
                { timeZoneId },
                EzDateTime,
                EzDateTime.buildTimeZoneInfoFromTimeZOneId);
        }

        let fullZoneId = momentTimeZoneInfo.name;

        let abbrZoneId = moment.tz(fullZoneId).format(EzChar.z);

        let zoneOffset = moment.tz(fullZoneId).utcOffset()

        let isDst = moment().tz(fullZoneId).isDST();

        let displayValue;

        let utcArray;

        if (isAutomatic) {
            displayValue = `Automatic (${EzString.stringWithTextOrDefault(displayNameOverride, fullZoneId)})`;

            utcArray = [
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE,
                fullZoneId,
                abbrZoneId
            ];
        } else {
            displayValue = EzString.stringWithTextOrDefault(displayNameOverride, fullZoneId);

            utcArray = [
                fullZoneId,
                abbrZoneId
            ];
        }

        return {
            value: displayValue,
            text: fullZoneId,
            zoneId: {
                full: fullZoneId,
                abbr: abbrZoneId
            },
            abbr: abbrZoneId,
            offset: zoneOffset,
            isdst: isDst,
            isAutomatic: isAutomatic,
            utc: utcArray
        };
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default supported time zones array constant
     * @returns {Array}
     * An array of time zone info objects: the automatic (browser detected) zone followed by each ezClocker supported US and Guam time zone.
     */
    static get DEFAULT_SUPPORTED_TIME_ZONES() {
        return [
            EzDateTime.AUTOMATIC_TIME_ZONE_INFO,
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.Pacific_Honolulu, 'US Hawaiian Time (Honolulu)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Anchorage, 'US Alaskan Time (Anchorage)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Los_Angeles, 'US Pacific Time (Los Angeles)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Denver, 'US Mountain Time (Denver)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Phoenix, 'US Mountain Standard Time (Phoenix)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_Chicago, 'US Central Time (Chicago)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.America_New_York, 'US Eastern Time (New York)'),
            EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.timeZoneIds.Pacific_Guam, 'Chamorro Standard Time (Guam)')
            // "American/Samoa" (UTC-11)
            // Chamorro Standard Time (UTC+10)
            // Atlantic Standard Time (Puerto Rico) (UTC-4)
            // Hawaii-Aleutian Standard Time (UTC-10)

            /*
            {
                value: 'US Hawaiian Time (Honolulu)',
                abbr: 'HST',
                offset: EzNumber.MINUS_10,
                isdst: false,
                text: '(UTC-10:00) Hawaii',
                utc: [
                    'Pacific/Honolulu'
                ]
            },
            {
                value: 'US Alaskan Time (Anchorage)',
                abbr: 'AKDT',
                offset: EzNumber.MINUS_8,
                isdst: true,
                text: '(UTC-09:00) Alaska',
                utc: [
                    'America/Anchorage'
                ]
            },
            {
                value: 'US Pacific Time (Los Angeles)',
                abbr: 'PDT',
                offset: EzNumber.MINUS_7,
                isdst: true,
                text: '(UTC-07:00) Pacific Time (US & Canada)',
                utc: [
                    'America/Los_Angeles'
                ]
            },
            {
                value: 'US Mountain Time (Denver)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Time (US & Canada)',
                utc: [
                    'America/Denver'
                ]
            },
            {
                value: 'US Mountain Standard Time (Phoenix)',
                abbr: 'MDT',
                offset: EzNumber.MINUS_6,
                isdst: true,
                text: '(UTC-07:00) Mountain Standard Time (US & Canada)',
                utc: [
                    'America/Phoenix'
                ]
            },
            {
                value: 'US Central Time (Chicago)',
                abbr: 'CDT',
                offset: EzNumber.MINUS_5,
                isdst: true,
                text: '(UTC-06:00) Central Time (US & Canada)',
                utc: [
                    'America/Chicago'
                ]
            },
            {
                value: 'US Eastern Time (New York)',
                abbr: 'EDT',
                offset: EzNumber.MINUS_4,
                isdst: true,
                text: '(UTC-05:00) Eastern Time (US & Canada)',
                utc: [
                    'America/New_York'
                ]
            },
            {
                value: 'Chamorro Standard Time (Guam)',
                abbr: 'WPST',
                offset: EzNumber.TEN,
                isdst: false,
                text: '(UTC+10:00) Guam, Port Moresby',
                utc: [
                    'Pacific/Guam'
                ]
            }
            */
        ];
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets an array of ezClocker supported time zone abbreviations.
     * From timezones.json, object:
     *      {
     *          "value": <string>,
     *          "abbr": <string>, (value stored in this array)
     *          "offset": <number>,
     *          "isdst": <boolean>,
     *          "text": <string>,
     *          "utc": [
     *              <string>,
     *              ...
     *              <string>
     *          ]
     *     }
     * @returns {Array}
     * An array of the ezClocker supported time zone abbreviations (e.g. 'HST', 'PDT', 'CDT', 'EDT').
     */
    static get EZCLOCKER_SUPPORTED_TIMEZONES_ABBR() {
        return [
            // Hawaiian Standard Time
            EzDateTime.timeZoneIds.HST,
            // Alaskan Standard Time
            EzDateTime.timeZoneIds.AKDT,
            // Pacific Daylight Time
            EzDateTime.timeZoneIds.PDT,
            // Pacific Standard Time
            EzDateTime.timeZoneIds.PST,
            // US Mountain Standard Time
            EzDateTime.timeZoneIds.UMST,
            // Mountain Standard Time
            EzDateTime.timeZoneIds.MDT,
            // Central Standard Time
            EzDateTime.timeZoneIds.CDT,
            // Canada Central Standard Time
            EzDateTime.timeZoneIds.CCST,
            // Eastern Daylight Time
            EzDateTime.timeZoneIds.EDT,
            // US Eastern Standard Time
            EzDateTime.timeZoneIds.UEDT,
            // West Pacific Standard Time
            EzDateTime.timeZoneIds.WPST
        ];
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Patches an isoDateTime that was URL decoded, replacing the + with a space. Returns the plus to the ISO date.
     * If the provided isoDateTime is undefined or empty, an EzString.EMPTY is returned.
     * Does not validate if the provided isoDateTime is a valid iso date!
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {string}
     * The isoDateTime with spaces restored to + characters, the original value if no patching was needed, or EzString.EMPTY if it had no text.
     */
    static patchBadDecodedIso(isoDateTime = EzString.EMPTY) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        // EzChar exposes these as lowercase getters (space/plus/forward_slash/backslash).
        // The previous uppercase names resolved to undefined, so every includes() call became
        // includes('undefined') === false and this method returned its input unpatched.
        if (!isoDateTime.includes(EzChar.space)) {
            // If no spaces exist, then no patching needed
            return isoDateTime;
        }

        // Plus characters in a iso date time string sometimes get replaced by a space due to HTML URL encoding issues.
        // Replace all spaces with the + character
        return !isoDateTime.includes(EzChar.forward_slash) || !isoDateTime.includes(EzChar.backslash)
            ? isoDateTime.replaceAll(EzChar.space, EzChar.plus)
            : isoDateTime;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred display date time foramt
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @type {string}
     */
    static #ezPreferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME;
    /**
     * @static
     * @public
     * @description
     * Gets the preferred display date time foramt
     * @returns {string}
     * The preferred moment.js display date time format string (default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME).
     */
    static get ezPreferredDisplayDateTimeFormat() {
        return this.#ezPreferredDisplayDateTimeFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred display date time foramt
     * @param {string} preferredDisplayDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     */
    static set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        this.#ezPreferredDisplayDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDisplayDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred display date time foramt
     * @returns {string}
     * The preferred moment.js display date time format string (default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    get ezPreferredDisplayDateTimeFormat() {
        return EzDateTime.ezPreferredDisplayDateTimeFormat;
    }

    /**
     * @public
     * @description
     * Sets the preferred display date time foramt
     * @param {string} preferredDisplayDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        EzDateTime.ezPreferredDisplayDateTimeFormat = preferredDisplayDateTimeFormat;
    }

    /**
     * @private
     * @description
     * Stores the preferred display time format
     * Default: EzDateTime.DEFAULT_HH_MM_DISPLAYFORMAT
     * @type {string}
     */
    static #ezPreferredTimeFormat = EzDateTime.DEFAULT_HH_MM_DISPLAYFORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred date time format string
     * @returns {string}
     * The preferred moment.js time format string (default: EzDateTime.DEFAULT_HH_MM_DISPLAYFORMAT).
     */
    static get ezPreferredTimeFormat() {
        return this.#ezPreferredTimeFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date time format string
     * @param {string} ezPreferredTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     */
    static set ezPreferredTimeFormat(ezPreferredTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        this.#ezPreferredTimeFormat = EzString.stringWithLengthOrDefault(
            ezPreferredTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_FORMAT);
    }

    /**
     * @static
     * @private
     * @description
     * Stores
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred date time format string
     * @returns {string}
     * The preferred moment.js date format string (default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT).
     */
    static get ezPreferredDateFormat() {
        return this.#ezPreferredDateFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     */
    static set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        this.#ezPreferredDateFormat = EzString.stringWithLengthOrDefault(
            preferredDateFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred date time format string
     * @returns {string}
     * The preferred moment.js date format string (default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    get ezPreferredDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }

    /**
     * @public
     * @description
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = preferredDateFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Sets the preferred display date format for moment objects
     * @param {string} ezPreferredDateFormat
     * Dfeault: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezSetPreferredDisplayDateFormat(ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = ezPreferredDateFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Gets the preferred display date format for moment objects
     * Default: 'MM/DD/YYYY'
     * Example: '06/07/2021'
     * @returns {string}
     * The preferred moment.js display date format string (EzDateTime.ezPreferredDateFormat).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezGetPreferredDisplayDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred date time format string
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred date time format string
     * @returns {string}
     * The preferred moment.js date and time format string (default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT).
     */
    static get ezPreferredDateTimeFormat() {
        return this.#ezPreferredDateTimeFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     */
    static set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        this.#ezPreferredDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred date time format string
     * @returns {string}
     * The preferred moment.js date and time format string (default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    get ezPreferredDateTimeFormat() {
        return EzDateTime.ezPreferredDateTimeFormat;
    }

    /**
     * @public
     * @description
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        EzDateTime.ezPreferredDateTimeFormat = preferredDateTimeFormat;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred date time with seconds format
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred date time with seconds format
     * @returns {string}
     * The preferred moment.js date and time with seconds format string (default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT).
     */
    static get ezPreferredDateTimeWithSecondsFormat() {
        return this.#ezPreferredDateTimeWithSecondsFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date time with seconds format
     * @param {string} preferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    static set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        this.#ezPreferredDateTimeWithSecondsFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeWithSecondsFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred date time with seconds format
     * @returns {string}
     * The preferred moment.js date and time with seconds format string (default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     */
    get ezPreferredDateTimeWithSecondsFormat() {
        return EzDateTime.#ezPreferredDateTimeWithSecondsFormat;
    }

    /**
     * @public
     * @description
     * Sets the preferred date time with seconds format
     * @param {string} preferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        EzDateTime.ezPreferredDateTimeWithSecondsFormat = preferredDateTimeWithSecondsFormat;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred date picker format
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @type {string}
     */
    static #ezPreferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred date picker format
     * @returns {string}
     * The preferred jQuery UI date picker format string (default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT).
     */
    static get ezPreferredDatePickerFormat() {
        return this.#ezPreferredDatePickerFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date picker format
     * @param {string} preferredDatePickerFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     */
    static set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        this.#ezPreferredDatePickerFormat = EzString.stringWithLengthOrDefault(
            preferredDatePickerFormat,
            EzDateTime.DEFAULT_DATE_PICKER_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred date picker format
     * @returns {string}
     * The preferred jQuery UI date picker format string (default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    get ezPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred date picker format
     * @param {string} preferredDatePickerFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = preferredDatePickerFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Gets the preferred date picker display format string.
     * Default: 'mm/dd/yy'
     * Example: '06/07/2021'
     * @returns {string}
     * The preferred jQuery UI date picker format string (EzDateTime.ezPreferredDatePickerFormat).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    ezGetPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Sets the preferred date picker display format
     * @param {string} preferredDatePickerFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    ezSetPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = EzString.hasLength(preferredDatePickerFormat)
            ? preferredDatePickerFormat
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred day name and date format
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredMomentDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT;

    /**
     * @static
     * @public
     * @description
     * Gets the preferred day name and date for
     * @returns {string}
     */

    static get ezPreferredMomentDayNameAndDateFormat() {
        return this.#ezPreferredMomentDayNameAndDateFormat;
    }

    /**
     * @static
     * @public
     * @description
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     */
    static set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        this.#ezPreferredMomentDayNameAndDateFormat = EzString.stringWithLengthOrDefault(
            preferredDayNameAndDateFormat,
            EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT);
    }

    /**
     * @public
     * @readonly
     * @description
     * Gets the preferred day name and date for
     * @returns {string}
     * The preferred moment.js day name and date format string (default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    get ezPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }

    /**
     * @public
     * @description
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the preferred day name plus date format string for moment objects.
     * Default format: 'dddd MM/DD/YYYY'
     * Example: Monday '06/07/2021'
     * @returns {string}
     * The preferred moment.js day name and date format string (EzDateTime.ezPreferredMomentDayNameAndDateFormat).
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezGetPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }

    /**
     * @public
     * @function
     * @description
     * Sets the preferred moment Day Name and Date format string.
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezSetPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }

    /**
     * @static
     * @private
     * @description
     * Stores the preferred locale to use (a valid enum property value from EzLocale).
     * Default: EzLocale.DEFAULT_LOCALE
     * @type {string}
     */
    static #ezPreferredLocale = EzLocale.DEFAULT_LOCALE;
    /**
     * @static
     * @public
     * @description
     * Gets the preferred locale to use
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    static get ezPreferredLocale() {
        return this.#ezPreferredLocale;
    }
    /**
     * @static
     * @public
     * @description
     * Sets the preferred locale to use
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCALE
     */
    static set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        this.#ezPreferredLocale = EzString.stringWithLengthOrDefault(
            ezLocale,
            EzDateTime.DEFAULT_LOCAL);

        /* TODO: Finish adding locale support
        // Inject the locale into the browser document
        this.#ezInjectMomentLocaleIntoDocument();

        // Set the moment.js locale to the preferred
        moment.locale(this.#ezPreferredLocale);
        */
    }
    /**
     * @public
     * @description
     * Gets the preferred locale to us
     * @returns {string}
     * The preferred locale, a valid enum property value from EzLocale.
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    get ezPreferredLocale() {
        return EzDateTime.ezPreferredLocale;
    }
    /**
     * @public
     * @description
     * Sets the preferred locale to us
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCAL
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        EzDateTime.ezPreferredLocale = ezLocale;
    }

    /**
     * @static
     * @private
     * @description
     * Stores if the perferred time zone was loaded from options or not
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredTimeZoneOption = false;
    /**
     * @static
     * @public
     * @description
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     * True if the preferred time zone was already loaded from the employer options; otherwise, false.
     */
    static get ezLoadedPreferredTimeZoneOption() {
        return this.#ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @static
     * @public
     * @description
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     */
    static set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        this.#ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @public
     * @description
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     * True if the preferred time zone was already loaded from the employer options; otherwise, false.
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    get ezLoadedPreferredTimeZoneOption() {
        return EzDateTime.ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @public
     * @description
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        EzDateTime.ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {boolean} triggerEvents
     * True to trigger the onSupportedTimeZonesReady, onActiveTimeZoneLoaded and onActiveTimeZoneChanged events; otherwise, false.
     * Default: true
     */
    static ezLoadPreferredTimezoneOption(triggerEvents = true) {
        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzLogger.info(
                EzString.msg`
                    Skipped loading the preferred time zone option: Option is already loaded.`);

            return;
        }

        if (!globalThis?.ezApi?.ezclocker) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Requried dependency globalThis.ezApi.ezclocker is not available yet.`);

            return;
        }

        if (!globalThis.ezApi.ezclocker?.ezClockerContext) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Required dependency globalThis.ezApi.ezclocker.ezClockerContext is not available yet.`);

            return;
        }

        let loadedActiveTimeZone;

        if (EzObject.isValid(globalThis.ezApi.ezclocker.ezClockerContext?.ezSelectedEmployerAccount)) {
            // Load from the selected employer account
            loadedActiveTimeZone = globalThis.ezApi.ezclocker.ezClockerContext.ezReadSelectedEmployerAccountOption(
                EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE,
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE);
        } else if (EzObject.isValid(globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer)) {
            // Load from the active employer account
            loadedActiveTimeZone = globalThis.ezApi.ezclocker.ezClockerContext.ezReadActiveEmployerOption(
                EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE,
                EzDateTime.DEFAULT_ACTIVE_TIMEZONE);
        } else {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time due to the following state:
                    The neither the globalThis.ezApi.ezclocker.ezClockerContext.ezSelectedEmployerAccount or the
                    globalThis.ezApi.ezclocker.ezClockerContext?.activeEmployer account is available at this time.`);

            return;
        }

        if (triggerEvents) {
            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onSupportedTimeZonesReady,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    'Supported time zones read.',
                    EzDateTime.ezSupportedTimeZones));
        }

        EzDateTime.ezActiveTimeZoneId = EzDateTime.isValidTimeZone(loadedActiveTimeZone)
            ? loadedActiveTimeZone
            : EzDateTime.DEFAULT_ACTIVE_TIMEZONE;

        EzDateTime.ezLoadedPreferredTimeZoneOption = true;

        if (triggerEvents) {
            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onActiveTimeZoneLoaded,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    'Active time zone loaded from options.',
                    EzDateTime.ezActiveTimeZoneId));

            globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                EzDateTime.ezEventNames.onActiveTimeZoneChanged,
                globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                    EzDateTime.ezApiName,
                    `Set EzDateTime's active time zone id and library moment.js's active time zone to: ${EzDateTime.ezActiveTimeZoneId}`,
                    EzDateTime.ezActiveTimeZoneId));
        }
    }
    /**
     * @public
     * @function
     * @description
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.ezLoadPreferredTimezoneOption
     */
    ezLoadPreferredTimezoneOption() {
        EzDateTime.ezLoadPreferredTimezoneOption();
    }

    /**
     * @static
     * @private
     * @description
     * Stores if the preferred locale option was loaded yet
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredLocaleOption = false;
    /**
     * @static
     * @public
     * @description
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     * True if the preferred locale option was already loaded; otherwise, false.
     */
    static get ezLoadedPreferredLocaleOption() {
        return this.#ezLoadedPreferredLocaleOption;
    }
    /**
     * @static
     * @public
     * @description
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     */
    static set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        this.#ezLoadedPreferredLocaleOption = EzBoolean.booleanOrFalse(loadedPreferredLocaleOption);
    }
    /**
     * @public
     * @description
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     * True if the preferred locale option was already loaded; otherwise, false.
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    get ezLoadedPreferredLocaleOption() {
        return EzDateTime.ezLoadedPreferredLocaleOption;
    }
    /**
     * @public
     * @description
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        EzDateTime.ezLoadedPreferredLocaleOption = loadedPreferredLocaleOption;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Guesses the local time zone
     * @returns {string}
     * The browser's local IANA time zone id as guessed by moment.js, e.g. 'America/Chicago'.
     */
    static ezGuessLocalTimeZone() {
        return moment.tz.guess(true);
    }
    /**
     * @public
     * @function
     * @description
     * Guesses the local time zone
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTimeZone.ezGuessLocalTimeZone
     */
    ezGuessLocalTimeZone = EzDateTime.ezGuessLocalTimeZone;

    /**
     * @static
     * @private
     * @function
     * @description
     * Sets the default time zone id for the moment.js library.
     * @param {string} momentJsDefaultTimeZone
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @returns {boolean}
     * True if the moment.js default time zone id was set. False otherwise.
     */
    static #setMomentJsDefaultTimeZone(momentJsDefaultTimeZone = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasLength(momentJsDefaultTimeZone) ||
            EzChar.ZERO === momentJsDefaultTimeZone ||
            EzChar.SPACE === momentJsDefaultTimeZone ||
            EzDateTime.DEFAULT_ACTIVE_TIMEZONE === momentJsDefaultTimeZone
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                momentJsDefaultTimeZone,
                EzDateTime.ezGuessLocalTimeZone());

        const FAILED_LOG_MSG = `Failed to set the default time zone id of ${activeTimeZoneIdToUse} for momentJs.`;

        try {
            moment.tz.setDefault(activeTimeZoneIdToUse);
        } catch (err) {
            try {
                EzLogger.warn(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err.message}.
                        Falling back to guessed local time zone from browser.`);

                moment.tz.setDefault(EzDateTime.ezGuessLocalTimeZone());

                EzLogger.info(`Set moment.js library's active time zone to: ${activeTimeZoneIdToUse}`);
                return true;
            } catch (err2) {
                EzLogger.error(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err2.message}.`);
            }
        }

        return false;
    }

    /**
     * @private
     * @description
     * Stores the current active time zone id
     * @returns {string}
     */
    static #ezActiveTimeZoneId = null;
    /**
     * @public
     * @description
     * Gets the current active time zone id
     * @returns {string}
     * The active time zone id; defaults to the guessed local browser time zone if none has been set.
     */
    static get ezActiveTimeZoneId() {
        if (null == this.#ezActiveTimeZoneId) {
            this.#ezActiveTimeZoneId = EzDateTime.ezGuessLocalTimeZone();
        }

        return this.#ezActiveTimeZoneId;
    }
    /**
     * @public
     * @description
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     */
    static set ezActiveTimeZoneId(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasText(activeTimeZoneId) || EzChar.ZERO === activeTimeZoneId
            || EzDateTime.DEFAULT_ACTIVE_TIMEZONE === activeTimeZoneId
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                activeTimeZoneId,
                EzDateTime.ezGuessLocalTimeZone());

        if (this.#ezActiveTimeZoneId !== activeTimeZoneIdToUse) {
            this.#ezActiveTimeZoneId = activeTimeZoneIdToUse;

            if (this.#setMomentJsDefaultTimeZone(this.#ezActiveTimeZoneId) && globalThis?.ezApi?.ezclocker?.ezEventEngine) {
                globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                    EzDateTime.ezEventNames.onActiveTimeZoneChanged,
                    globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                        EzDateTime.ezApiName,
                        `Active time zone id changed to ${this.#ezActiveTimeZoneId}.`,
                        this.#ezActiveTimeZoneId));
            }
        }
    }
    /**
     * @public
     * @description
     * Gets the current active time zone id
     * @returns {string}
     * The active time zone id (EzDateTime.ezActiveTimeZoneId).
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    get activeTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public
     * @description
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    set activeTimeZone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public
     * @function
     * @description
     * Sets the active timezone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezSetActiveTimezone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public
     * @function
     * @description
     * Gets the active timezone for the context
     * @returns {string}
     * The active time zone id (EzDateTime.ezActiveTimeZoneId).
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public
     * @function
     * @description
     * Gets the active timezone for the context
     * @returns {string}
     * The active time zone id (EzDateTime.ezActiveTimeZoneId).
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public
     * @function
     * @description
     * Gets the active timezone for the context
     * @returns {string}
     * The active time zone id (EzDateTime.ezActiveTimeZoneId).
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimezone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public
     * @function
     * @description
     * Preferred method to use to obtain the active time zone id.
     * Gets the active timezone for the context
     * @returns {string}
     * The active time zone id (EzDateTime.ezActiveTimeZoneId).
     * @deprecated
     * Migrate to static property: EzDateTime.ezActiveTimeZoneId
     */
    ezGetActiveTimezoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns the event names triggered by EzDateTime
     * @returns {object}
     * An object whose properties are the names of the events triggered by EzDateTime.
     * @deprecated
     * Migrate to static property: EzDateTime.ezEventNames property
     */
    get ezEventNames() {
        return EzDateTime.ezEventNames;
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns the browser's default language (locale)
     * @returns {string}
     * The browser's navigator.language value (e.g. 'en-US'), or EzLocale.DEFAULT_LOCALE if it is not available.
     */
    get ezBrowserLanguage() {
        const lang = navigator.language;

        return EzString.hasLength(lang)
            ? lang
            : EzLocale.DEFAULT_LOCALE;
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns EzLocale based upon the browser default language
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    get ezBrowserLocale() {
        const browserLang = EzDateTime.ezInstance.ezBrowserLanguage;

        return EzLocale.ezLocaleForValue(
            2 <= browserLang.length
                ? browserLang.slice(0, 2).toUpperCase()
                : browserLang.toUpperCase());
    }

    /**
     * @private
     * @description
     * Stores the all the known time zones
     * Default: EzArray.EMPTY
     * @type {Array}
     */
    static #ezAllKnownTimeZones = EzArray.EMPTY;
    /**
     * @static
     * @public
     * @description
     * Gets the all the known time zones
     * @returns {Array}
     * The array of all known time zones (default: EzArray.EMPTY).
     */
    static get ezAllKnownTimeZones() {
        return this.#ezAllKnownTimeZones;
    }
    /**
     * @static
     * @public
     * @description
     * Sets the all the known time zones
     * @param {Array} allKnownTimeZones
     * Default: EzArray.EMPTY
     */
    static set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        this.#ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }
    /**
     * @public
     * @description
     * Gets the all the known time zones
     * @returns {Array}
     * The array of all known time zones (default: EzArray.EMPTY).
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    get ezAllKnownTimeZones() {
        return EzDateTime.ezAllKnownTimeZones;
    }
    /**
     * @public
     * @description
     * Sets the all the known time zones
     * @param {Array} allKnownTimeZones
     * Default: EzArray.EMPTY
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        EzDateTime.ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the time zone info object for the UTC time zone.
     * @returns {object}
     * The time zone info object (see EzDateTime.buildTimeZoneInfoFromTimeZoneId) for the UTC time zone.
     */
    static get utcTimeZoneInfo() {
        return EzDateTime.buildTimeZoneInfoFromTimeZoneId(EzDateTime.UTC);
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the local zone info object for ezClocker's AUTOMATIC time zone.
     * @returns {object}
     * The automatic time zone info object for the browser's guessed local time zone, or the UTC time zone info if the guess fails.
     */
    static get AUTOMATIC_TIME_ZONE_INFO() {
        try {
            return EzDateTime.buildTimeZoneInfoFromTimeZoneId(
                moment.tz.guess(true),
                EzString.EMPTY,
                true);
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Failed to guess the local time zone due to the following error: ${err.message}.
                    Automatic time zone will default to UTC instead.`);
        }

        return EzDateTime.utcTimeZoneInfo;
    }

    /**
     * @private
     * @description
     * Stores the supported time zones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @type {Array}
     */
    static #ezSupportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES;
    /**
     * @static
     * @public
     * @description
     * Gets the supported time zones
     * @returns {Array}
     * The array of supported time zone info objects (default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES).
     */
    static get ezSupportedTimeZones() {
        return this.#ezSupportedTimeZones;
    }
    /**
     * @static
     * @public
     * @description
     * Sets the supported time zones
     * @param {Array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     */
    static set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        this.#ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }
    /**
     * @public
     * @description
     * Gets the supported time zones
     * @returns {Array}
     * The array of supported time zone info objects (default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES).
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    get ezSupportedTimeZones() {
        return EzDateTime.ezSupportedTimeZones;
    }
    /**
     * @public
     * @description
     * Sets the supported time zones
     * @param {Array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        EzDateTime.ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the time zone info for a supported time zone id. Returns null if the timeZoneId is not supported.
     * @param {string} timeZoneId
     * The time zone id or abbreviation to look up, e.g. 'America/Chicago' or 'CDT'.
     * @param {boolean} [ignoreAutomatic]
     * Default: false
     *     When true, skips any time zone entry in DEFAULT_SUPPORTED_TIME_ZONES where
     *     isAutomatic === true (i.e. the synthetic "Automatic (detected)" entry built by
     *     buildTimeZoneInfoFromTimeZoneId with isAutomatic=true).
     *     Use this when you need a real, named IANA time zone match and must not resolve
     *     the caller's local/browser-detected zone as a valid result.
     *     When false (default), the Automatic entry is included in the search and can be
     *     returned if its utc array contains the requested timeZoneId or its offset matches.
     * @returns {object}
     * The matching supported time zone info object, or null if the timeZoneId is not supported.
     */
    static getSupportedTimeZoneInfo(timeZoneId, ignoreAutomatic = false) {
        if (!EzString.hasText(timeZoneId)) {
            // Blank, null, or undefined time zone ids (e.g. an employer whose preferred time zone option is not set)
            // can not match a supported time zone.
            return null;
        }

        const tzUpperCase = timeZoneId.toUpperCase();

        for (let supportedTimeZoneInfo of EzDateTime.ezSupportedTimeZones) {
            if(ignoreAutomatic && supportedTimeZoneInfo.isAutomatic) {
                continue;
            }
            if (supportedTimeZoneInfo.zoneId.full.toUpperCase() === tzUpperCase ||
                supportedTimeZoneInfo.zoneId.abbr.toUpperCase() === tzUpperCase) {
                return supportedTimeZoneInfo;
            }

            if (supportedTimeZoneInfo.name === timeZoneId) {
                return supportedTimeZoneInfo;
            }

            for (const tzId of supportedTimeZoneInfo.utc) {
                if (tzId.toUpperCase() === tzUpperCase) {
                    return supportedTimeZoneInfo;
                }
            }

            // Only compare offsets when moment-timezone recognizes the id. buildTimeZoneInfoFromTimeZoneId throws for
            // unrecognized ids (e.g. a legacy or corrupted saved option value) and this lookup should return null instead.
            if(timeZoneId !== EzDateTime.DEFAULT_ACTIVE_TIMEZONE && EzObject.isValid(moment.tz.zone(timeZoneId))) {
                const timeZoneInfo = EzDateTime.buildTimeZoneInfoFromTimeZoneId(timeZoneId, EzString.EMPTY);
                if(timeZoneInfo.offset === supportedTimeZoneInfo.offset) {
                    return supportedTimeZoneInfo;
                }
            }
        }

        return null;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets if the now date time is within a leap year or not.
     * @returns {boolean}
     * True if the current date in the active time zone is within a leap year; otherwise, false.
     */
    static get nowIsLeapYear() {
        return EzDateTime.now(EzDateTime.ezActiveTimeZoneId).isLeapYear();
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets if the provided aMoment is within a leap year or not.
     * @param {moment} aMoment
     * The moment instance to check. An EzBadParamException is thrown if it is not a valid moment.
     * @returns {boolean}
     * True if aMoment is within a leap year; otherwise, false.
     */
    static isLeapYear(aMoment = null) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime,
                `get ${EzDateTime.isLeapYear.toString()}`);
        }

        return aMoment.isLeapYear();
    }

    /**
     * @public
     * @function
     * @description
     * Initializes EzDateTime
     * @returns {EzDateTime}
     * The initialized EzDateTime singleton instance.
     */
    ezInit() {
        EzDateTime.ezInstance.ezRegisterEvents();

        EzDateTime.ezInstance.ezRegisterWantEvents();

        return EzDateTime.ezInstance;
    }

    /**
     * @public
     * @function
     * @description
     * Registers events triggered by this class
     */
    ezRegisterEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneChanged);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneLoaded);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onSupportedTimeZonesReady);
    }

    /**
     * @public
     * @function
     * @description
     * Registers want events for initialization
     */
    ezRegisterWantEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzDateTime.ezEventNames.onActiveTimeZoneChanged,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezDisplayActiveTimezone);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onSelectedEmployerAccountReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnSelectedEmployerReady,
            true);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onUserContextReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnUserContextReady,
            true);
    }

    /**
     * @public
     * @function
     * @description
     * Loads the authenticated users's preferred locale option
     */
    ezLoadAuthenticatedUserPreferredLocaleOption() {
        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredLocaleOption)) {
            // Already loaded
            return;
        }

        // Sets the locale from the browser's navigator.language property
        EzDateTime.ezPreferredLocale = EzDateTime.ezInstance.ezBrowserLocale;

        if (EzBoolean.isTrue(globalThis.ezApi.ezclocker['ezClockerContext']?.ready) &&
            EzBoolean.isTrue(globalThis.ezApi.ezclocker.ezClockerContext.ezGetUserContext().ready)) {

            // TODO: Finishe locale support: Add user level options and ready the preferred locale value

            EzDateTime.ezLoadedPreferredLocaleOption = true;
        }
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets the default option object for the JQuery Date Picker Input
     * @returns {object}
     * A jQuery UI date picker options object (dateFormat, defaultDate of today at start of day, constrainInput, changeMonth, changeYear).
     */
    static get defaultDatePickerOptions() {
        return {
            dateFormat: globalThis.ezApi.ezclocker.ezDateTime.ezGetPreferredDatePickerFormat(),
            defaultDate: globalThis.ezApi.ezclocker.ezDateTime.ezToJsDate(EzDateTime.nowWithStartOfDay()),
            constrainInput: false,
            changeMonth: true,
            changeYear: true
        };
    }

    /**
     * @public
     * @function
     * @description
     * Returns the default option object for the JQuery Date Picker Input
     * @returns {object}
     * The default jQuery UI date picker options object (EzDateTime.defaultDatePickerOptions).
     * @deprecated
     * Migrate to static property: EzDateTime.defaultDatePickerOptions
     */
    ezGetDefaultDatePickerOptions() {
        return EzDateTime.defaultDatePickerOptions;
    }

    /**
     * @public
     * @function
     * @description
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} dateTimePickerId
     * The HTML element id of the jQuery UI date picker input to initialize.
     * @param {object | null} eventHandlers
     * Optional object whose function properties are applied as date picker event handler options.
     * @param {object | null} propertyOverrides
     * Optional object whose properties override the default date picker options.
     * @param {moment|null} aInitialMoment
     * Optional initial moment to set as the date picker's value (at the start of day).
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment)
     */
    ezInitDateTimePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment) {
        return EzDateTime.initDatePicker(
            dateTimePickerId,
            eventHandlers,
            propertyOverrides,
            aInitialMoment);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to initialize.
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     */
    static initDatePicker(datePickerId, eventHandlers = null, propertyOverrides = null, aInitialMoment = null, isEndPeriod = false) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.initDatePicker);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id="${datePickerId}" to reference a valid date picker HTML input element.`,
                `Date picker HTML input element with id="${datePickerId}" does not exist.`,
                EzDateTime,
                EzDateTime.initDatePicker,
                `Failed to initialize a date picker HTML input element with id="${datePickerId}".`);
        }

        const datePickerOptions = EzDateTime.defaultDatePickerOptions;

        if (EzObject.isValid(eventHandlers)) {
            for (const eHandlers in eventHandlers) {
                if (EzFunction.isFunction(eventHandlers[eHandlers])) {
                    datePickerOptions[eHandlers] = eventHandlers[eHandlers];
                }
            }
        }

        if (EzObject.isValid(propertyOverrides)) {
            for (const pOverrides in propertyOverrides) {
                datePickerOptions[pOverrides] = propertyOverrides[pOverrides];
            }
        }

        // Initialize the datepicker with the options.
        const updateJQueryDatePicker = datePickerHTMLElement.datepicker(datePickerOptions);

        // Assign the initial value (if any)
        aInitialMoment = EzDateTime.isValidMoment(aInitialMoment)
            ? aInitialMoment
            : null;

        if (EzBoolean.isTrue(isEndPeriod)) {
            EzDateTime.setDatePickerValueEndOfDay(
                datePickerId,
                aInitialMoment);
        } else {
            EzDateTime.setDatePickerValueStartOfDay(
                datePickerId,
                aInitialMoment);
        }

        return updateJQueryDatePicker;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(datePickerId, eventHandlers, propertyOverrides, aInitialMoment, isEndPeriod)
     */
    ezInitDatePicker = EzDateTime.initDatePicker;

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided iso8601DateTime value
     * @param {undefined|null|string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * A moment instance created from isoDateTime in the active time zone (see EzDateTime.fromIso).
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezFromIso8601(isoDateTime = EzString.EMPTY) {
        return EzDateTime.fromIso(isoDateTime);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a new moment with the provided ISO string.
     * If the iso string is empty, null is returned. If the iso string is not valid, ezNowWithStartOfDay is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezCreateFromIso = EzDateTime.fromIso;

    /**
     * @public
     * @function
     * @description
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment|string}
     * A moment instance created from isoDateTime, or an empty string if isoDateTime is not a valid ISO 8601 value.
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrEmpty(isoDateTime)
     */
    ezCreateFromIsoOrEmpty(isoDateTime = EzString.EMPTY) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert to a moment instance.
     * @returns {moment|string}
     * A moment instance created from isoDateTime, or an empty string if isoDateTime is not a valid ISO 8601 value.
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIsoOrNull(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrNull(isoDateTime)
     */
    ezCreateFromIsoOrNull(isoDateTime) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided iso string OR returns the provided defaultMoment if the iso string is not valid.
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert to a moment instance.
     * @param {moment} defaultMoment
     * The value to return if isoDateTime is empty or not a valid ISO 8601 value.
     * @returns {moment}
     * A moment instance created from isoDateTime, or the defaultMoment if isoDateTime is not valid.
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     */
    ezCreateFromIsoOrDefaultMoment(isoDateTime, defaultMoment) {
        return EzDateTime.ezInstance.ezFromIsoOrDefault(
            isoDateTime,
            defaultMoment);
    }

    /**
     * Creates a moment instance from the provided iso8601DateTime value.
     * Sets the timezone to the preferred time zone.
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * @static
     * @public
     * @function
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance created from isoDateTime in the provided time zone, or now if isoDateTime is empty or not valid.
     */
    static fromIso(isoDateTime = null, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            timeZoneId,
            EzDateTime.now(timeZoneId));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided iso8601DateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default: EzDateTime.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to: EzDateTime.fromIso(isoDateTime, timeZoneId, ezLocale)
     */
    ezFromIso = EzDateTime.fromIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert to a moment instance.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string|moment}
     * A moment instance created from isoDateTime in the provided time zone, or an empty string if isoDateTime is not valid.
     */
    static fromIsoOrEmpty(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            timeZoneId,
            EzString.EMPTY);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {string|moment}
     * @deprecated
     * Migrate to static method: EzDateTime.fromIsoOrEmpty(isoDateTime, timeZoneId, ezLocale)
     */
    ezFromIsoOrEmpty = EzDateTime.fromIsoOrEmpty;

    /**
     * Creates a moment instance from the provided isoDateTime value
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * @public
     * @function
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|unknown} defaultMoment
     * IMPORTANT:
     * 1) The defaultMoment can be anything, does not necessarly need to be a valid date time instance.
     * 2) Caller has responsibility to make sure the defaultMoment is something they can handle properly.
     * 3) If the defaultMoment is a valid date time (moment) instance the time zone IS NOT set to the provided
     * timeZoneId (if any). It is returned as it was provided.
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment|unknown}
     * A moment instance created from isoDateTime in the provided time zone, or the defaultMoment if isoDateTime is empty, invalid, or fails to parse.
     */
    static fromIsoOrDefault(isoDateTime = null, defaultMoment = null, timeZoneId = EzDateTime.ezActiveTimeZoneId
        /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        if (!EzString.hasText(isoDateTime)) {
            return defaultMoment;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return defaultMoment;
        }

        /* TODO: Finish locale work
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }

        let momentInstance = moment(isoString).locale(ezLocale);
        */

        try {
            const momentInstance = moment(isoDateTime).tz(EzDateTime.safeTimeZoneId(timeZoneId));

            return EzDateTime.isValidMoment(momentInstance)
                ? momentInstance
                : defaultMoment;
        } catch (err) {
            if (!EzString.hasText(err.message)) {
                err.message = EzString.em`
                    Unexpected exception when attempting to create a date time (moment) instance
                    from the provided isoDateTime of ${isoDateTime} with time zone id ${timeZoneId}`;
            }

            const em = EzString.em`
                Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                due to the following error: ${err.message}`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em, err);
            } else {
                globalThis?.console?.error(em, err);
            }
        }

        return defaultMoment;
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided isoDateTime value
     * @param {string} isoDateTime
     * @param {unknown} defaultMoment
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.fromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId, ezLocale)
     */
    ezFromIsoOrDefault = EzDateTime.fromIsoOrDefault;

    /**
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @public
     * @function
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert to a moment instance.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {null|moment}
     * A moment instance created from isoDateTime in the provided time zone, or null if isoDateTime is empty, invalid, or fails to parse.
     */
    ezTimeStampFromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        if (!EzString.hasText(isoDateTime)) {
            return null;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return null;
        }

        try {
            const momentInstance = moment(isoDateTime)
                .tz(EzDateTime.safeTimeZoneId(timeZoneId));

            return EzDateTime.isValidMoment(momentInstance)
                ? momentInstance
                : null;
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                    due to the following error: ${err.message}`);

            return null;
        }
    }

    /**
     * Creates a moment instance from the provided isoDateTime value
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * @public
     * @function
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert to a moment instance.
     * @param {unknown} defaultMoment
     * The value to return if a moment instance cannot be created from isoDateTime.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance created from isoDateTime in the provided time zone, or the defaultMoment if one cannot be created.
     */
    ezTimeStampFromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezTimeStampFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        return null != momentFromIso
            ? momentFromIso
            : defaultMoment;
    }

    /**
     * @static
     * @description
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the input TZ and if absent, preferred time zone.
     * @public
     * @function
     * @param {string} isoDateTime
     * The ISO 8601 date time string to convert. An EzBadParamException is thrown if a valid moment cannot be created from it.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance created from isoDateTime in the provided time zone.
     */
    static fromIsoWithTimeZoneId(isoDateTime = null, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.fromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        if (!EzDateTime.isValidMoment(momentFromIso)) {
            throw new EzBadParamException(
                'isoDateTime',
                EzDateTime,
                EzDateTime.fromIsoWithTimeZoneId);
        }

        return momentFromIso;
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the input TZ and if absent, preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezFromIsoAndTimeZone = EzDateTime.fromIsoWithTimeZoneId;

    /**
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @static
     * @public
     * @function
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {null|moment}
     * A moment instance created from isoDateTime in the provided time zone, or null if isoDateTime is empty or not a valid ISO 8601 value.
     */
    static fromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        // TODO: Finish locale work
        return EzDateTime.fromIsoOrDefault(
            isoDateTime,
            null,
            timeZoneId,
            /* ezLocale */);
    }

    ezFromIsoOrNull = EzDateTime.fromIsoOrNull;

    /**
     * @public
     * @function
     * @description
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsIso8601(proposedValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedValue);
    }

    /**
     * @public
     * @function
     * @description
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsoString(proposedIsoDateTimeValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedIsoDateTimeValue);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     */
    static validateIso(proposedIsoDateTimeValue = EzString.EMPTY) {
        /**
         * @function
         * @description
         * Builds the validation failed result.
         * @returns {object}
         * The failed validation result object (valid: false, iso8601Value: null) with an explanatory message.
         */
        const ezBuildValidationFailedResult = () => {
            // Proposed value considered a valid ISO 8601 value
            return {
                proposedIsoDateTimeValue,
                proposedValue: proposedIsoDateTimeValue,
                valid: false,
                iso8601Value: null,
                message: `The proposed value of ${proposedIsoDateTimeValue} is not a valid ISO 8601 value.`
            };
        };

        if (!EzString.hasText(proposedIsoDateTimeValue)) {
            return ezBuildValidationFailedResult();
        }

        if (!EzDateTime.ISO_VALIDATION_REGEX.test(proposedIsoDateTimeValue)) {
            // If the regex fails, fall back to creating a Javascript Date from the proposed value.
            try {
                // See if Javascript's Date method will parse the value:
                const jsDate = new Date(proposedIsoDateTimeValue);

                if (!EzObject.isValid(jsDate) || 'Invalid Date' === jsDate.toString()) {
                    // All validation checks failed
                    return ezBuildValidationFailedResult();
                }
            } catch (err) {
                EzLogger.debug(
                    EzString.em`
                        Failed to create a new Javascript Date instance from the proposedValue ${proposedIsoDateTimeValue}.
                        Error reported: ${err.message}`,
                    err);

                // All validation checks failed
                return ezBuildValidationFailedResult();
            }
        }

        // Proposed value considered a valid ISO 8601 value
        return {
            proposedIsoDateTimeValue,
            proposedValue: proposedIsoDateTimeValue,
            valid: true,
            iso8601Value: proposedIsoDateTimeValue,
            message: `The proposed value of ${proposedIsoDateTimeValue} is a valid ISO 8601 value.`
        };
    }

    /**
     * @public
     * @function
     * @description
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIso = EzDateTime.validateIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     * True if proposedIsoDateTimeValue is a valid ISO 8601 date time string; otherwise, false.
     */
    static isValidIso(proposedIsoDateTimeValue = null) {
        return EzBoolean.isTrue(EzDateTime.validateIso(proposedIsoDateTimeValue).valid);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso
     */
    ezIsValidIso = EzDateTime.isValidIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Appends the time from the time picker to the provided moment instance.
     * @param {string} timePickerId
     * The HTML element id of the time picker input to read the hour and minute from.
     * @param {moment} momentInstance
     * The moment instance to apply the time picker's hour and minute to.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * The momentInstance in timeZoneId with the time picker's hour and minute applied (seconds and ms zeroed), or unchanged if the picker can't be read.
     */
    static appendTimeFromTimePicker(timePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime,
                EzDateTime.appendTimeFromTimePicker);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.appendTimeFromTimePicker);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const timePickerElement = EzUx.$id(timePickerId);

        const defaultResponse = EzDateTime.withZone(
            momentInstance,
            timeZoneId);

        if (!EzObject.isValid(timePickerElement) || EzNumber.ZERO === timePickerElement?.length) {
            EzLogger.error(
                EzString.msg`
                    Failed to locate a time picker input HTML element with id="${timePickerId}".
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        const hour = timePickerElement?.timepicker('getHour');

        const min = timePickerElement?.timepicker('getMinute');

        if (!EzObject.isValid(hour) || !EzObject.isValid(min)) {
            EzLogger.error(
                EzString.msg`
                    Failed to obtain the time picker input HTML element with id="${timePickerId}"'s hour and/or minute values.
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        return EzDateTime.withZone(momentInstance, timeZoneId)
            .hours(hour)
            .minutes(min)
            .seconds(0)
            .milliseconds(0);
    }

    /**
     * @public
     * @function
     * @description
     * Appends the time from the time picker to the provided moment instance.
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.appendTimeFromTimePicker(timePickerId, momentInstance, timeZoneId)
     */
    ezAppendTimeFromTimePicker = EzDateTime.appendTimeFromTimePicker;

    /**
     * @public
     * @readonly
     * @description
     * Returns the defaul options for time picker initialization.
     * @returns {object}
     * Result instance:
     *  {
     *      timeFormat: 'hh:mm tt',
     *      showPeriod: true,
     *      showLeadingZero: true
     *  }
     */
    static get defaultTimePickerOptions() {
        return {
            timeFormat: 'hh:mm tt',
            showPeriod: true,
            showLeadingZero: true
        };
    }

    /**
     * @public
     * @function
     * @description
     * Returns the defaul options for time picker initialization.
     * @returns {object}
     * The default time picker options object (EzDateTime.defaultTimePickerOptions).
     * @deprecated
     * Migrate to static property: EzDateTime.defaultTimePickerOptions
     */
    ezGetDefaultTimePickerOptions() {
        return EzDateTime.defaultTimePickerOptions;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Initializes a UX time picker with defaults. Applies any additional options or overrides to the default
     * options provided in timePickerOptionsOverrides.
     * @param {string} [timePickerId]
     * Default: EzString.EMPTY
     * @param {undefined|null|moment} [initialMoment]
     * Default: EzDateTime.now()
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     * @param {undefined|null|object} [timePickerOptionsOverrides]
     * Default: null
     * @returns {object}
     * Jquery referece to the time picker
     */
    static initTimePicker(timePickerId = EzString.EMPTY, initialMoment = null, timePickerOptionsOverrides = null) {
        if (!EzString.hasText(timePickerId)) {
            throw new EzBadParamException(
                { timePickerId },
                EzDateTime,
                EzDateTime.initTimePicker);
        }

        const timePickerElement = EzUx.$id(timePickerId);

        if (!EzObject.isValid(timePickerElement)) {
            throw new EzBadStateException(
                EzString.em`
                    Expected the provided timePickerId of "${timePickerId}" to be a valid HTML element id or jQuery selector
                    that is associated with a valid HTML element.`,
                EzString.em`
                    However, the provided timePickerId of ${timePickerId} is either
                    a valid HTML element or jQuery selector but not associated with a valid HTML elementnot
                    or is not valid HTML element id or jQuery selector.`,
                EzDateTime,
                EzDateTime.initTimePicker);
        }

        const timePickerOptions = EzDateTime.defaultTimePickerOptions;

        if (EzObject.isValid(timePickerOptionsOverrides)) {
            for (const prop in timePickerOptionsOverrides) {
                if (EzString.hasText(prop) && EzObject.isValid(timePickerOptionsOverrides?.[prop])) {
                    timePickerOptions[prop] = timePickerOptionsOverrides[prop];
                }
            }
        }

        // Apply the time picker options
        timePickerElement.timepicker(timePickerOptions);

        EzDateTime.setTimePickerValue(
            timePickerId,
            EzDateTime.dateTimeOrNow(initialMoment));

        return timePickerElement;
    }

    /**
     * @public
     * @function
     * @description
     * Initializes a UX time picker with defaults. Applies any additional options or overrides to the default
     * options provided in timePickerOptionsOverrides.
     * @param {string} timePickerId
     * @param {undefined|null|moment} initialMoment
     * Default: EzDateTime.now()
     * @param {undefined|null|object} timePickerOptionsOverrides
     * Default: null
     * @returns {object}
     * Jquery referece to the time picker
     * @deprecated
     * Migrate to static method: EzDateTime.initTimePicker(timePickerId, initialMoment, timePickerOptionsOverrides)
     */
    ezInitTimePicker = EzDateTime.initTimePicker;

    /**
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @public
     * @function
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance at the start of day, in the provided time zone, for the date selected in the date picker.
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 2) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 3) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull)
     */
    static dateFromDatePicker(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.fromDatePickerStartOfDay(
            datePickerId,
            false,
            timeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * @param {string} datePickerId
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 2) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 3) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull)
     * 4) (deprecated) EzDateTime.dateFromDatePicker(datePickerId, timeZoneId)
     */
    ezDateFromDatePicker = EzDateTime.dateFromDatePicker;

    /**
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * The time is set to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @public
     * @function
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance at the start of day, in the provided time zone, for the date selected in the date picker.
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 1) (preferred) EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull)
     * 4) (deprecated) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId)
     * 5) (deprecated) EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    static momentFromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.ezInstance.fromDatePickerStartOfDay(
            datePickerId,
            EzBoolean.booleanOrOrFalse(returnNullInsteadOfTodayIfNull),
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * The time is set to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 2) (deprecated) EzDateTime.momentFromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezMomentFromDatePicker = EzDateTime.fromDatePickerStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment object from a date picker value with the specified timezone
     * @param {string} datePickerId - The ID of the date picker element
     * @param {string} [timeZoneId] - The timezone ID to use
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment.Moment} A moment object in the specified timezone
     * @deprecated
     * Migrate to one the following static method:
     * EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    static momentFromDatePickerWithTimeZone(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                this.ezInstance,
                this.momentFromDatePickerWithTimeZone);
        }

        return EzDateTime.fromDatePicker(
            datePickerId,
            true,
            timeZoneId);

        /* Original Implementation
         * return moment(EzUx.getInputValue(datePickerId)).tz(timeZoneId);
         */
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment object from a date picker value with the specified timezone
     * @param {string} datePickerId - The ID of the date picker element
     * @param {string} [timeZoneId] - The timezone ID to use
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment.Moment} A moment object in the specified timezone
     * @deprecated
     * Migrate to one of the following:
     * 1) (preferred) EzDateTime.fromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 3) (deprecated) EzDateTime.momentFromDatePickerWithTimeZone(datePickerId, timeZoneId)
     */
    ezMomentFromDatePickerWithTimeZone = EzDateTime.momentFromDatePickerWithTimeZone;

    /**
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @public
     * @function
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {null|moment}
     * A moment instance, in the provided time zone, for the date selected in the date picker, or the default (null or today) if no date is available.
     */
    static fromDatePicker(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.fromDatePicker);
        }

        const defaultMoment = EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull)
            ? EzDateTime.nowWithStartOfDay(timeZoneId)
            : null;

        const emDefaultResponseInfo = null == defaultMoment
            ? 'Will instead return the default response of null instead.'
            : `Will instead return the provided default date time of "${EzDateTime.nowWithStartOfDay(timeZoneId)}" instead.`;

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const datePickerElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerElement?.[EzNumber.FIRST_INDEX])) {
            // Did not find a date picker input element for the provided id
            EzLogger.warn(
                EzString.em`
                    Failed to obtain the date and time value from a date picker input HTML element due to the following error:
                    No date picker input element is associated with the HTML element id="${datePickerId}.
                    ${emDefaultResponseInfo}`);

            return defaultMoment;
        }

        // Obtain the jsDate from the jQuery date picker
        const jsDateFromDatePicker = datePickerElement.datepicker('getDate');

        if (!EzObject.isValid(jsDateFromDatePicker)) {
            EzLogger.warn(
                EzString.em`
                    Failed to obtain a Javascript date instance from the jQuery date picker element with id="${datePickerId}"
                    due to the following error: call to datepicker('getdate') returned an undefined or null date instance.
                    ${emDefaultResponseInfo}`);

            return defaultMoment;
        }

        /*
         * TODO: Support locale date formats. Currently, the date picker input's value is expected to be in the format of MM/DD/YYYY since that's the format
         * that EzClocker sets on the date picker inputs. If the date pickers are configured to use a different date format, then this will need to be updated
         * to support that.
         */

        // Note: Since the date picker always stores the selected date in a Javascript Date instance AND the Javascript Date instance will only ever have the
        // date time in the browser's default time zone if we use the Javascript Date and change the time zone to a different configured time zone the actual
        // date will incorrectly change. We need the date in the picker but in the configured time zone, so the moment is created from the date pickers
        // raw input value.
        return moment.tz(
            datePickerElement?.[EzNumber.FIRST_INDEX]?.value || EzDateTime.safeGetInputValue(datePickerId),
            EzDateTime.DTFORMAT_MM_DD_YYYY,
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the value of an HTML input associated with the provied inputElementId.
     * Uses EzUI or EzUx to obtain the value of the input element (which evern is available).
     * If the input element does not exist or the provided inputElementId is not valid, then the defaultValue is returned (default is empty string).
     * @param {string} inputElementId
     * The id of the input element.
     * @param {string} [defaultValue]
     * Default: EzString.EMPTY
     * The default value to return if the input element is not found or invalid.
     * @returns {string} The value of the input element or the default value.
     */
    static safeGetInputValue(inputElementId, defaultValue = EzString.EMPTY) {
        defaultValue = EzObject.assignOrDefault(
            defaultValue,
            EzString.EMPTY);

        if (!EzString.hasText(inputElementId)) {
            globalThis?.console?.error(
                EzString.em`
                    A valid, non-null element id is required in call to EzDateTime.safeGetInputValue(..).
                    Returning the default value of "${defaultValue}" instead.`);

            return defaultValue;
        }

        const elementExists = EzUx.elementExists(inputElementId);

        if (!elementExists) {
            globalThis?.console?.error(
                EzString.em`
                    Input element id ${inputElementId} is not associated with an existing HTML intput.
                    Returning the default value of "${defaultValue}" instead.`);

            return defaultValue;
        }

        return EzUx.getInputValue(inputElementId);
    }

    /**
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the start of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @public
     * @function
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance at the start of day, in the provided time zone, for the date selected in the date picker.
     */
    static fromDatePickerStartOfDay(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.fromDatePickerStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.fromDatePicker(
                datePickerId,
                EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull),
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the start of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {undefined|null|string} datePickerId
     * Default: null
     * @param {undefined|null|boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId)
     */
    ezMomentFromDatePickerStartOfDay = EzDateTime.fromDatePickerStartOfDay;

    /**
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the end of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @public
     * @function
     * @param {string} datePickerId
     * Default: null
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment instance at the end of day, in the provided time zone, for the date selected in the date picker.
     */
    static fromDatePickerEndOfDay(datePickerId = null, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.fromDatePickerEndOfDay);
        }

        return EzDateTime.withTimeAtEndOfDay(
            EzDateTime.fromDatePicker(
                datePickerId,
                EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull),
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the jQuery date picker with the provided datePickerId.
     * If a moment instance is returned the time is forced to the end of day.
     * Pass value returnNullInsteadOfTodayIfNull=true to have the call return null instead of today if it is unable to obtain a valid date from the date
     * picker (e.g. if the date picker has no date selected and thus returns null or an invalid date).
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * @param {string} datePickerId
     * Default: null
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    ezMomentFromDatePickerEndOfDay = EzDateTime.fromDatePickerEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @returns {string}
     * The ISO 8601 date string (YYYY-MM-DD) built from the provided jsDate's local year, month and day.
     */
    static jsDateToIsoDate(jsDate = null) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.ezJsDateToIsoDate);
        }

        const yearStr = EzNumber.asStringOrDefault(
            jsDate.getFullYear(),
            moment().year())
            .padStart(EzNumber.FOUR, EzChar.ZERO);

        const monthStr = EzNumber.asStringOrDefault(jsDate.getMonth() + EzNumber.ONE, '01')
            .padStart(EzNumber.TWO, EzChar.ZERO);

        const dayStr = EzNumber.asStringOrDefault(
            jsDate.getDate(),
            '01')
            .padStart(EzNumber.TWO, EzChar.ZERO);

        return `${yearStr}-${monthStr}-${dayStr}`;
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * Default: null
     * A valid Javascript Date instance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.jsDateToIsoDate(jsDate)
     */
    ezJsDateToIsoDate = EzDateTime.jsDateToIsoDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * The ISO 8601 date time string for jsDate in the provided time zone with the given hours, minutes, seconds and milliseconds applied.
     */
    static jsDateToIsoDateTime(jsDate = null, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.jsDateToIsoDateTime);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        return moment.tz(jsDate, timeZoneId)
            .hours(EzNumber.numberOrZero(hours))
            .minutes(EzNumber.numberOrZero(minutes))
            .seconds(EzNumber.numberOrZero(seconds))
            .milliseconds(EzNumber.numberOrZero(milliseconds))
            .format();
    }

    /**
     * @static
     * @description
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * The JavaScript Date to convert; its local (user agent) time zone offset is used for the zone portion.
     * @param {object} options
     * Default: {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     * The jsDate as an ISO 8601 string containing only the parts enabled in options, e.g. 2024-03-05T08:30:00-06:00.
     */
    static jsDateToIso(jsDate, options) {
        if (!EzObject.isValid(jsDate)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.jsDateToIso);
        }

        if (!EzObject.isValid(options)) {
            options = {
                year: true,
                month: true,
                date: true,
                hours: true,
                minutes: true,
                seconds: true,
                milliseconds: false,
                zone: true
            };
        }

        let jsDateAsIso = EzString.EMPTY;

        let dateDelimiter = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.year)) {
            jsDateAsIso = `${jsDate.getFullYear()}`;

            dateDelimiter = EzChar.DASH;
        }

        if (EzBoolean.booleanOrFalse(options?.month)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoMonthString(jsDate.getMonth() + EzNumber.ONE)}`;

            dateDelimiter = EzChar.DASH;
        }

        if (EzBoolean.booleanOrFalse(options?.date)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoDateString(jsDate.getDate())}`;
        }

        let jsTimeAsIso = EzString.EMPTY;

        let timeDelimiter = EzChar.T;

        if (EzBoolean.booleanOrFalse(options?.hours)) {
            jsTimeAsIso = `${timeDelimiter}${EzNumber.toIsoHourString(jsDate.getHours())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.minutes)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMinuteString(jsDate.getMinutes())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.seconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoSecondString(jsDate.getSeconds())}`;

            timeDelimiter = EzChar.COLON;
        }

        if (EzBoolean.booleanOrFalse(options?.milliseconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMillisecondString(jsDate.getMilliseconds())}`;
        }

        let jsTimeZoneAsIso = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.zone)) {
            const timeZoneOffset = -jsDate.getTimezoneOffset();

            const hourOffset = Math.floor(Math.abs(timeZoneOffset) / EzNumber._60);

            const minuteOffset = Math.abs(timeZoneOffset) % EzNumber._60;

            jsTimeZoneAsIso = `${EzNumber.toIsoHoursString(hourOffset)}:${EzNumber.toIsoMinuteString(minuteOffset)}`;

            const timeZoneOffsetPrefix = EzNumber.ZERO <= timeZoneOffset
                ? EzChar.PLUS
                : EzChar.DASH;

            jsTimeZoneAsIso = '00:00' === jsTimeZoneAsIso
                ? EzChar.Z
                : `${timeZoneOffsetPrefix}${jsTimeZoneAsIso}`;
        }

        return `${jsDateAsIso}${jsTimeAsIso}${jsTimeZoneAsIso}`;
    }

    /**
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * @param {object} options
     * options = {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.jsDateToIso(jsDate, options)
     */
    ezJsDateToIso = EzDateTime.jsDateToIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * If the date picker has no date selected, null is returned.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @param {string} timeZoneId
     * Optionally in the specified timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId with the date picker's year, month, and date and the time at 00:00:00.000; or null if the picker has no date.
     */
    static dateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay);
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (null == jsDate) {
            return null;
        }

        // Step 1: Create a moment with time at beginning of day in the users's selected time zone
        return EzDateTime.nowWithStartOfDay(
            EzDateTime.safeTimeZoneId(timeZoneId))
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * @param {string} timeZoneId
     * Optionally in the specified timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezDateTimeFromDatePickerWithTimeAtStartOfDay = EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @param {string} timeZoneId
     * Optionally return the date in the provided timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId with the date picker's year, month, and date and the time at 23:59:59.999.
     */
    static dateTimeFromDatePickerWithTimeAtEndOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay);
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            throw new EzException(
                EzString.em`
                    The date picker (id=${datePickerId}') did not return a valid Javascript date instance from the call to
                    EzUx.$id(${datePickerId}).datepicker('getDate')`);
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        return EzDateTime.nowWithEndOfDay(timeZoneId)
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timeZoneId
     * Optionally return the date in the provided timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezDateTimeFromDatePickerWithTimeAtEndOfDay = EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay;

    /**
     * @public
     * @function
     * @description
     * Gets the date picker element's date as a string with a language-sensitive representation of the date portion
     * of the specified date in the user agent's timezone. In otherwords, returns a locale specific display date (which
     * may not be mm/dd/yyyy).
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @returns {string}
     * The picker's date as a locale-specific display date (Date.toLocaleDateString()), or today's display date if the picker or its date is missing.
     */
    ezGetDatePickerLocaleDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerLocaleDisplayDate);
        }

        if (!EzUx.elementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        return jsDate.toLocaleDateString();
    }

    /**
     * @public
     * @function
     * @description
     * Gets the date picker element's date as a string in ezClocker's default display date format.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        ENGINEERING NOTES
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        JQuery UI date pickers store the value as a Javascript Date in the local time zone.
        EzClocker sets date pickers with the display date only (ignoring the time and zone)
        When the date is read from the date picker, the time and zone must get ignored to
        nake sure the date doesn't translate when converting it to the moment in the user's
        selected time zone.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to read the date from.
     * @returns {string}
     * The picker's date formatted with ezClocker's preferred display date format, or today's display date if the picker or its date is missing.
     */
    ezGetDatePickerDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerDisplayDate);
        }

        if (!EzUx.elementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = EzUx.$id(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        const momentInstance = EzDateTime.nowWithEndOfDay();

        // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
        return EzDateTime.toDisplayDate(
            momentInstance.year(jsDate.getFullYear())
                .month(jsDate.getMonth())
                .date(jsDate.getDate()));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerDateFormat
     * The jQuery UI date picker dateFormat string to convert, e.g. 'mm/dd/yy'.
     * @returns {string}
     * The equivalent moment.js date format string, e.g. 'MM/DD/YYYY' for 'mm/dd/yy'.
     */
    static datePickerDateFormatToMomentDateFormat(datePickerDateFormat) {
        if (!EzObject.isValid(datePickerDateFormat)) {
            throw new EzBadParamException(
                'datePickerDateFormat',
                EzDateTime,
                EzDateTime.datePickerDateFormatToMomentDateFormat);
        }

        return datePickerDateFormat
            .replace(/DD/, 'dddd')
            .replace(/D/, 'ddd')
            .replace(/dd/, 'DD')
            .replace(/d/, EzChar.D)
            .replace(/MM/, 'MMMM')
            .replace(/M/, 'MMM')
            .replace(/mm/, 'MM')
            .replace(/m/, EzChar.M)
            .replace(/yy/, 'YYYY')
            .replace(/y/, 'YY')
            .replace(/o/, 'DDD')
            .replace(/oo/, 'DDDD')
            .replace(/@/, EzChar.X)
            .replace(/!/, EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
     */
    ezDatePickerDateFormatToMomentDateFormat = EzDateTime.datePickerDateFormatToMomentDateFormat;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose dateFormat option is read.
     * @returns {string}
     * The date picker's jQuery UI dateFormat option value, or EzDateTime.DEFAULT_DATE_PICKER_FORMAT if the picker has no date format.
     */
    static getDatePickerDateFormat(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getDatePickerDateFormat);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            EzLogger.error(
                EzString.em`
                    Failed to obtain a date picker input HTML element's date format string for id="${datePickerId}"
                    due to the folowing error: Date picker HTML element with id="${datePickerId}" does not exist.
                    Returning the default date format string of ${EzDateTime.DEFAULT_MM_DD_YYYY} instead.`);
        }

        const datePickerDateFormatStr = datePickerHTMLElement.datepicker(
            'option',
            'dateFormat');

        return EzString.hasText(datePickerDateFormatStr)
            ? datePickerDateFormatStr
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @public
     * @function
     * @description
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDatePickerDateFormat(datePickerId)
     */
    ezGetDatePickerDateFormat = EzDateTime.getDatePickerDateFormat;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose date format is converted.
     * @returns {string}
     * The moment.js equivalent of the date picker's dateFormat, or EzDateTime.DEFAULT_DATE_PICKER_FORMAT if the picker has no date format.
     */
    static getMomentJsDateFormatForDatePicker(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getMomentJsDateFormatForDatePicker);
        }

        const datePickerDateFormat = EzDateTime.getDatePickerDateFormat(datePickerId);

        return EzString.hasLength(datePickerDateFormat)
            ? EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDateFormatForDatePicker(datePickerId)
     */
    ezGetMomentDateFormatForDatePickerDateFormat = EzDateTime.getMomentJsDateFormatForDatePicker;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Unless the provided momentInstance's time is needed, use one of the following instead of this method:
     *  1) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueStartOfDay(...)
     *  2) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueEndOfDay(...)
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to set.
     * @param {moment} momentInstance
     * The moment whose date (time forced to start of day) is set in the date picker; an invalid moment clears the picker's date.
     */
    static setDatePickerValue(datePickerId, momentInstance) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValue);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValue,
                `Date picker HTML element with id=${datePickerId} does not currently exist.`);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Unless the provided momentInstance's time is needed, use one of the following instead of this method:
     *  1) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueStartOfDay(...)
     *  2) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueEndOfDay(...)
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValue(datePickerId, momentInstance)
     */
    ezSetDatePickerValue = EzDateTime.setDatePickerValue;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to set.
     * @param {moment} momentInstance
     * The moment whose date, at start of day in timeZoneId, is set in the date picker; an invalid moment clears the picker's input.
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerDateWithTimeAtStartOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerDateWithTimeAtStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtStartOfDay(
                momentInstance,
                timeZoneId);

            const currentPickerMoment = EzDateTime.dateTimeFromDatePickerWithTimeAtStartOfDay(
                datePickerId,
                timeZoneId);

            if (EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    ezSetDatePickerDateWithTimeAtStartOfDay = EzDateTime.setDatePickerDateWithTimeAtStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to set.
     * @param {moment} momentInstance
     * The moment whose date, at end of day in timeZoneId, is set in the date picker; an invalid moment clears the picker's input.
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerDateWithTimeAtEndOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setDatePickerDateWithTimeAtEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtEndOfDay(
                momentInstance,
                timeZoneId);

            let currentPickerMoment;

            try {
                currentPickerMoment = EzDateTime.dateTimeFromDatePickerWithTimeAtEndOfDay(
                    datePickerId,
                    timeZoneId);
            } catch (err) {
                const em = EzString.em`
                    Failed to obtain a moment at end of day for date pciker with id=
                    ${datePickerId} due to the following error: ${err.message}.
                    Defaulting currentPickerMoment to null instead.`;

                if (EzObject.isValid(globalThis?.ezApi?.ezclocker?.ezLogger)) {
                    globalThis.ezApi.ezclocker.ezLogger.debug(em);
                } else if (EzObject.isValid(globalThis?.console)) {
                    globalThis?.console?.debug(em);
                }

                currentPickerMoment = null;
            }

            if (EzDateTime.isValidMoment(currentPickerMoment) && EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerDateWithTimeAtEndOfDay(datePickerId, momentInstance, timeZoneId)
     */
    ezSetDatePickerDateWithTimeAtEndOfDay = EzDateTime.setDatePickerDateWithTimeAtEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose input text is set.
     * @param {moment} momentInstance
     * The moment (time at start of day) formatted with the picker's date format as the input text; an invalid moment sets empty text.
     */
    static setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate);
        }

        let momentAtStartOfDayWithPTZDisplayDate = EzString.EMPTY;

        if (EzDateTime.isValidMoment(momentInstance)) {
            momentAtStartOfDayWithPTZDisplayDate = EzDateTime.withTimeAtStartOfDay(momentInstance)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId));
        }

        // Set the date pickers input text box equal to the same value if it not already the same value
        if (momentAtStartOfDayWithPTZDisplayDate !== EzString.stringOrEmpty(EzUx.getInputValue(datePickerId))) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                momentAtStartOfDayWithPTZDisplayDate);
        }
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance)
     */
    ezSetDatePickerInputValueWithMomentDisplayDate = EzDateTime.setDatePickerInputValueWithMomentDisplayDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose date and input text are set.
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { datePickerId },
                EzDateTime,
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected date picker id "${datePickerId}" to reference a valid date picker HTML element`,
                `Date picker HTML element with id="${datePickerId}" does not exist.`,
                EzDateTime,
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate,
                `Failed to set the date picker HTML element for id="${datePickerId}"'s input and date value.`);
        }

        // Set to null instead of empty string so the date date picker's selected date is cleard when the provided momentInstance is null or undefined
        const datePickerDisplayDate = EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(momentInstance, timeZoneId)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId))
            : EzString.EMPTY;

        // Set the actual date of the date picker using the datePickerDisplayDate value
        // Calling datepicker('setDate', datePickerDisplayDate) should not trigger a onChange event for the input
        datePickerHTMLElement.datepicker(
            'setDate',
            datePickerDisplayDate);

        // Set the date pickers input text value to the datePickerDisplayDate value (if not already equal that)
        if (EzString.stringOrEmpty(EzUx.getInputValue(datePickerId)) !== datePickerDisplayDate) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                datePickerDisplayDate);
        }
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance, timeZoneId)
     */
    ezSetDatePickerInputAndDateValueWithMomentDisplayDate = EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to set.
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueStartOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueStartOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueStartOfDay = EzDateTime.setDatePickerValueStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input to set.
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueEndOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const currentPickerMoment = EzDateTime.fromDatePickerEndOfDay(datePickerId, true);

            if (EzDateTime.isValidMoment(currentPickerMoment) && EzDateTime.equals(momentInstance, currentPickerMoment)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            }
        }
    }

    /**
     * @public
     * @function
     * @description
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueEndOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueEndOfDay = EzDateTime.setDatePickerValueEndOfDay;

    /**
     * @public
     * @function
     * @description
     * Sets the date picker's minimum date with time at start of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose minDate option is set.
     * @param {moment} momentInstance
     * The moment whose date, with time at start of day, becomes the date picker's minimum selectable date.
     */
    ezSetDatePickerMinDateStartOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        EzUx.$id(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toJsDate(
                EzDateTime.withTimeAtStartOfDay(momentInstance)));
    }

    /**
     * @public
     * @function
     * @description
     * Sets the date picker's minimum date with time at endof day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose minDate option is set.
     * @param {moment} momentInstance
     * The moment whose date, with time at end of day, becomes the date picker's minimum selectable date.
     */
    ezSetDatePickerMinDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        EzUx.$id(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the date picker's maximum date with time at end of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input whose maxDate option is set.
     * @param {moment} momentInstance
     * The moment whose date, with time at end of day, becomes the date picker's maximum selectable date.
     */
    static setDatePickerMaxDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasText(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }
        if (!EzUx.elementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id "${datePickerId}" to reference a valid date picker HTML input element.`,
                `HTML element id "${datePickerId}" doe snot reference a valid HTML element.`,
                EzDateTime,
                EzDateTime.setDatePickerMaxDateEndOfDay,
                `Failed to set the date picker HTML input element with id=${datePickerId}'s maxDate property equal to ${EzDateTime.toIso(momentInstance)}.`);
        }

        datePickerHTMLElement.datepicker(
            'option',
            'maxDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    ezSetDatePickerMaxDateEndOfDay = EzDateTime.setDatePickerMaxDateEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     * A JavaScript Date for the momentInstance (after converting it to the preferred time zone).
     */
    static toJsDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toJsDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance).toDate();
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     * @deprecated
     * Migrate to static method: EzDateTime.toJsDate(momentInstance)
     */
    ezToJsDate = EzDateTime.toJsDate;

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input that supplies the date.
     * @param {string} timePickerId
     * The HTML element id of the time picker input that supplies the hour and minute.
     * @param {undefined|null|string} timeZone
     * Optional time zone id for the resulting moment, passed to EzDateTime.fromDatePickerStartOfDay (active time zone when not provided).
     * @returns {moment}
     * A moment with the date picker's date and the time picker's hour and minute (seconds and milliseconds set to 0).
     */
    ezDateTimeFromPickers(datePickerId, timePickerId, timeZone) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        const datePickerMoment = EzDateTime.fromDatePickerStartOfDay(datePickerId, false, timeZone);

        let hour = EzUx.$id(timePickerId).timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = EzNumber.ZERO;
        }

        let min = EzUx.$id(timePickerId).timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = EzNumber.ZERO;
        }

        return datePickerMoment.hour(hour)
            .minute(min)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * The HTML element id of the jQuery UI date picker input that supplies the date.
     * @param {string} timePickerId
     * The HTML element id of the time picker input (note: the hour and minute are currently read from 'edit_clockInTime').
     * @returns {moment}
     * A moment in the active time zone with the date picker's date and the time picker's hour and minute (seconds and milliseconds set to 0).
     */
    ezMomentFromPickers(datePickerId, timePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }

        const datePickerMoment = EzDateTime.fromDatePickerStartOfDay(datePickerId);

        let hour = EzUx.$id('edit_clockInTime').timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = EzNumber.ZERO;
        }

        let min = EzUx.$id('edit_clockInTime').timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = EzNumber.ZERO;
        }

        return datePickerMoment
            .hour(hour)
            .minute(min)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to  EzDateTime.toDisplayDate()
     */
    ezDateString = EzDateTime.toDisplayDate;

    static get NEVER_SET_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(EzNumber.ZERO)
            // Janurary (zero based month)
            .month(EzNumber.ZERO)
            .date(EzNumber.ONE)
            .hour(EzNumber.ZERO)
            .minute(EzNumber.ZERO)
            .seconds(EzNumber.ZERO)
            .millisecond(EzNumber.ZERO);
    }

    static get NEVER_SET_DATE_ISO() {
        return EzDateTime.toIsoDate(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static get NEVER_SET_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static getNeverSetDateTimeIso(noZone, noMilliseconds, noMillisecondsOrSeconds, noTime) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_SET_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_SET_DISPLAY_DATE() {
        return EzChar.DASH;
    }

    static get NEVER_EXPIRE_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(EzNumber._9999)
            // Janurary (zero based month)
            .month(EzNumber.ZERO)
            .date(EzNumber.ONE)
            .hour(EzNumber._23)
            .minute(EzNumber._59)
            .seconds(EzNumber._59)
            .millisecond(EzNumber._999);
    }

    static get NEVER_EXPIRE_DATE_ISO() {
        return EzDateTime.toIsoDate(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    static get NEVER_EXPIRE_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the Never expire date time as an ISO
     * @param {boolean} [noZone]
     * Default: false
     * @param {boolean} [noTime]
     * Default: false
     * @param {boolean} [noMillisecondsOrSeconds]
     * Default: false
     * @param {boolean} [noMilliseconds]
     * Default: false
     * @returns {string}
     * The never-expire date (9999-01-01) as an ISO string, shaped by the noTime, noMillisecondsOrSeconds, noMilliseconds, and noZone flags.
     */
    static getNeverExpireDateTimeIso(noZone = false, noTime = false, noMillisecondsOrSeconds = false, noMilliseconds = false) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_EXPIRE_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_EXPIRE_DISPLAY_DATE() {
        return 'NEVER';
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals EzChar.DASH
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     * True if momentInstanceOrString represents the never-expire date (9999-01-01); otherwise, false.
     */
    static isNeverExpire(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_EXPIRE_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-9999' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('9999/01/01') ||
                momentInstanceOrString.startsWith('9999.01.01') ||
                momentInstanceOrString.startsWith('01/01/9999') ||
                momentInstanceOrString.startsWith('01-01-9999') ||
                momentInstanceOrString.startsWith('01.01.9999');
        }

        return EzDateTime.isValidMoment(momentInstanceOrString)
            ? EzDateTime.NEVER_EXPIRE_DATE_ISO === EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString))
            : false;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals EzChar.DASH
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverExpire(momentInstanceOrString)
     */
    ezIsNeverExpire = EzDateTime.isNeverExpire;

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     * True if momentInstanceOrString represents the never-set date (0000-01-01); otherwise, false.
     */
    static isNeverSet(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_SET_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-0000' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverSetDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverSetDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverSetDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('0000/01/01') ||
                momentInstanceOrString.startsWith('0000.01.01') ||
                momentInstanceOrString.startsWith('01/01/0000') ||
                momentInstanceOrString.startsWith('01-01-0000') ||
                momentInstanceOrString.startsWith('01.01.0000');
        } else if (EzDateTime.isValidMoment(momentInstanceOrString)) {
            const isoDate = EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString));

            return EzDateTime.NEVER_SET_DATE_ISO === isoDate || '0000-01-01' === isoDate;
        }

        return false;
    }

    /**
     * @public
     * @function
     * @description
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverSet(momentInstanceOrString)
     */
    ezIsNeverSet = EzDateTime.isNeverSet;

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided isoDateTime to a displayable date
     * @param {undefined|null|string} isoDateTime
     * The ISO 8601 date time string to transform; undefined, null, or blank values return an empty string.
     * @returns {string}
     * The display date for isoDateTime, the never-expire/never-set display date for sentinel values, or isoDateTime unchanged if parsing fails.
     */
    static isoToDisplayDate(isoDateTime) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        if (EzDateTime.isNeverExpire(isoDateTime)) {
            return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
        }

        if (EzDateTime.isNeverSet(isoDateTime)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            return EzDateTime.toDisplayDate(momentInstance);
        } catch (err) {
            const em = EzString.em`
                Unable to transform iso date time '${isoDateTime} to a display date
                due to the following error: ${err.message}`;

            if (globalThis?.ezApi?.ezclocker?.ezLogger) {
                globalThis.ezApi.ezclocker.ezLogger.error(em);
            } else if (globalThis?.console) {
                globalThis.console.error(em);
            }

            return isoDateTime;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided isoDateTime to a displayable date
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.isoToDisplayDate(isoDateTime)
     */
    ezIsoToDisplayDate = EzDateTime.isoToDisplayDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided isoDateTime to a displayable string in format: MM/YYYY
     * @param {undefined|null|string} isoDateTime
     * The ISO 8601 date time string to transform; undefined, null, or blank values return an empty string.
     * @returns {string}
     * The MM/YYYY display string for isoDateTime, the never-expire/never-set display value, or isoDateTime unchanged if parsing fails.
     */
    static isoToDisplayMonthYear(isoDateTime) {
        if (!EzString.hasText(isoDateTime)) {
            return EzString.EMPTY;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            if (EzDateTime.isNeverExpire(momentInstance)) {
                return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
            }

            if (EzDateTime.isNeverSet(momentInstance)) {
                return EzDateTime.NEVER_SET_DISPLAY_DATE;
            }

            return EzDateTime.toDisplayMonthYear(momentInstance);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Unable to transform iso date time '${isoDateTime} to a display month and year format
                    due to the following error: ${err.message}`);

            return isoDateTime;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided isoDateTime to a displayable string in format: MM/YYYY
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.isoToDisplayMonthYear(isoDateTime)
     */
    ezIsoToDisplayMonthYear = EzDateTime.isoToDisplayMonthYear;

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided momentInstance to a display string in formation: MM/YYYY
     * @param {moment} momentInstance
     * The moment to format as a month and year display string.
     * @returns {string}
     * The momentInstance in the preferred time zone formatted with EzDateTime.DEFAULT_MONTH_YEAR_DISPLAY_FORMAT, or a never-expire/never-set value.
     */
    static toDisplayMonthYear(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayMonthYear);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MONTH_YEAR_DISPLAY_FORMAT);
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided momentInstance to a display string in formation: MM/YYYY
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayMonthYear(momentInstance)
     */
    ezToDisplayMonthYear = EzDateTime.ezToDisplayMonthYear;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the string formatted using the ezPreferredTimeFormat (default = hh:mm) a for the provided moment.js instance.
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     * The momentInstance's time in the preferred time zone formatted with EzDateTime.ezPreferredTimeFormat ('11:59 a'/'12:00 a' for never-expire/never-set).
     */
    static toDisplayTime(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDate);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return '11:59 a';
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return '12:00 a';
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredTimeFormat);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the string formatted ezPreferredDateFormat (default = MM/DD/YYYY) for the provided moment.js instance.
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     * The momentInstance's date in the preferred time zone formatted with EzDateTime.ezPreferredDateFormat, or the never-expire/never-set display date.
     */
    static toDisplayDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDate);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredDateFormat);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDate(momentInstance)
     */
    ezToDisplayDate = EzDateTime.toDisplayDate;

    /**
     * @public
     * @function
     * @description
     * Get the name of the day
     * @param {moment} momentInstance
     * The moment whose day of the week name is returned.
     * @returns {string}
     * The full day of the week name (moment.js format 'dddd') in the preferred time zone, e.g. Monday.
     */
    static toDayNameDisplay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDayNameDisplay);
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('dddd');
    }

    /**
     * @public
     * @function
     * @description
     * Get the name of the day
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDayNameDisplay(momentInstance)
     */
    ezToDayNameDisplay = EzDateTime.toDayNameDisplay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the day name plus date string for display using the preferred moment day name and ate format value.
     * @param {moment} momentInstance
     * The moment to format as a day name and date display string.
     * @returns {string}
     * The momentInstance in the preferred time zone formatted with EzDateTime.ezPreferredMomentDayNameAndDateFormat.
     */
    static toDisplayDayNameAndDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toDisplayDayNameAndDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredMomentDayNameAndDateFormat);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the day name plus date string for display using the preferred moment day name and ate format value.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDayNameAndDate(momentInstance)
     */
    ezToDisplayDayNameAndDate = EzDateTime.toDisplayDayNameAndDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a date range in format: "{sDate} - {eDate}"
     * @param {moment} sMoment
     * The moment for the start date of the range.
     * @param {moment} eMoment
     * The moment for the end date of the range.
     * @returns {string}
     * The start and end dates formatted with EzDateTime.ezPreferredDateFormat and joined as '{sDate} - {eDate}'.
     */
    static toDisplayDateRange(sMoment, eMoment) {
        if (!EzDateTime.isValidMoment(sMoment)) {
            throw new EzBadParamException(
                'sMoment',
                EzDateTime,
                EzDateTime.toDisplayDateRange);
        }

        if (!EzDateTime.isValidMoment(eMoment)) {
            throw new EzBadParamException(
                'eMoment',
                EzDateTime,
                EzDateTime.toDisplayDateRange);
        }

        const displayStartDate = EzDateTime.momentWithPTZ(sMoment)
            .format(EzDateTime.ezPreferredDateFormat);

        const displayEndDate = EzDateTime.momentWithPTZ(eMoment)
            .format(EzDateTime.ezPreferredDateFormat);

        return `${displayStartDate} - ${displayEndDate}`;
    }

    /**
     * @public
     * @function
     * @description
     * Returns a date range in format: "{sDate} - {eDate}"
     * @param {moment} sMoment
     * @param {moment} eMoment
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDateRange(sMoment, eMoment)
     */
    ezToDisplayDateRange = EzDateTime.toDisplayDateRange;

    /**
     * @public
     * @function
     * @description
     * Returns the string hh:mm a format of the provided moment. If the moment is not valid, then the current time is used.
     * @param {moment} momentInstance
     * The moment whose time is formatted.
     * @returns {string}
     * The momentInstance's time in the preferred time zone formatted with EzDateTime.DTFORMAT_hh_mm_a, e.g. 08:30 pm.
     */
    static toTimeString(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toTimeString);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DTFORMAT_hh_mm_a);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the string hh:mm a format of the provided moment. If the moment is not valid, then the current time is used.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeString(momentInstance)
     */
    ezTimeString = EzDateTime.toTimeString;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     * The momentInstance in the preferred time zone formatted with optionalDateTimeFormat (or EzDateTime.ezPreferredDateTimeFormat).
     */
    static toDisplayDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.toDisplayDateTime);
        }

        const dateTimeFormatToUse = EzString.stringWithTextOrDefault(
            optionalDateTimeFormat,
            EzDateTime.ezPreferredDateTimeFormat);

        try {
            return EzDateTime.momentWithPTZ(momentInstance).format(dateTimeFormatToUse);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}
                    [momentInstance: ${EzDateTime.toIso(momentInstance)}]
                    [Date time format string: ${dateTimeFormatToUse}]`);

            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.toDisplayDateTime);
        }
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDateTime(momentInstance, optionalDateTimeFormat)
     */
    ezToDisplayDateTime = EzDateTime.toDisplayDateTime;

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     * The momentInstance in the preferred time zone formatted with optionalDateTimeFormat, or an empty string if invalid or formatting fails.
     */
    ezToDisplayTimeStampDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return EzString.EMPTY;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {unknown} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string|unknown}
     * The formatted display date time string, or defaultValue if momentInstance is invalid or formatting fails.
     */
    ezToDisplayDateTimeOrDefault(momentInstance = null, defaultValue = EzString.EMPTY, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     * The momentInstance in the preferred time zone formatted with optionalDateTimeFormat (or EzDateTime.ezPreferredDateTimeWithSecondsFormat).
     */
    ezToDisplayDateTimeWithSeconds(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {unknown} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string|unknown}
     * The formatted display date time with seconds, or defaultValue if momentInstance is invalid or formatting fails.
     */
    ezToDisplayDateTimeWithSecondsOrDefault(momentInstance = null, defaultValue = EzString.EMPTY,
        optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     * The formatted display date time with seconds, or an empty string if momentInstance is invalid or formatting fails.
     */
    ezToDisplayDateTimeWithSecondsOrEmpty(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        return EzDateTime.ezInstance.ezToDisplayDateTimeWithSecondsOrDefault(
            momentInstance,
            EzString.EMPTY,
            optionalDateTimeFormat);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * The moment to convert to an ISO 8601 date time string.
     * @returns {string}
     * The momentInstance as an ISO 8601 date time string in the active time zone (see EzDateTime.toIso).
     * @deprecated
     * Migrate to static method: EzDateTime.toIso()
     */
    ezToIsoDateTime(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided moment to a ISO date string in the format of YYYY-MM-DD
     * Template: "YYYY-MM-DD"
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     * The momentInstance's date in the preferred time zone formatted as 'YYYY-MM-DD', or an empty string if momentInstance is invalid.
     */
    static toIsoDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.format('YYYY-MM-DD');
    }

    ezToIsoDate = EzDateTime.toIsoDate;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIso(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIsoOrDefault(
            momentInstance,
            timeZoneId,
            EzString.EMPTY);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIso(momentInstnace, timeZoneId)
     */
    ezToIso = EzDateTime.toIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultValue
     * Default: EzDateTime.nowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIsoOrDefault(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId, defaultValue = EzDateTime.nowAsIso()) {
        try {
            return !EzDateTime.isValidMoment(momentInstance)
                ? EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    EzString.stringWithLengthOrDefault(
                        defaultValue,
                        EzDateTime.nowAsIso(timeZoneId)))
                : EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    momentInstance.tz(EzDateTime.safeTimeZoneId(timeZoneId))
                        .format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT));
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Unable to convert the provided momentInstance to an ISO string due to the following error:
                    ${EzString.stringOrDefault(err?.message, 'Unnamed excception without message')}`,
                err);

            return EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                EzString.stringWithLengthOrDefault(
                    defaultValue,
                    EzDateTime.nowAsIso(timeZoneId)));
        }
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Transforms the provided isoDateTimeWithUTCTimeZone based upon the following logic:
     * 1) If the provied isoDateTimeWithUTCTimeZone has text:
     * ====> 1a) If the provided isoDateTimeWithUTCTimeZone includes text '+00:00' then transform the isoDateTimeWithUTCTimeZone's '+00:00' text to EzChar.Z and return
     * the updated isoDateTimeWithUTCTimeZone value.
     * ====> 1b) If the provided isoDateTimeWithUTCTimeZone does not includes text '+00:00' then return isoDateTimeWithUTCTimeZone value as provided.
     * 2) If the provied isoDateTimeWithUTCTimeZone is undefined, null, or is only whitespace then eturn isoDateTimeWithUTCTimeZone value as provided.
     * @param {undefined|null|string} isoDateTimeWithUTCTimeZone
     * The ISO 8601 date time string whose '+00:00' UTC offset should be replaced with 'Z'.
     * Default: null
     * @returns {undefined|null|string}
     * The isoDateTimeWithUTCTimeZone with '+00:00' replaced by 'Z', or the provided value unchanged.
     */
    static isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(isoDateTimeWithUTCTimeZone = null) {
        return EzString.hasText(isoDateTimeWithUTCTimeZone) && isoDateTimeWithUTCTimeZone.includes('+00:00')
            ? isoDateTimeWithUTCTimeZone.replace('+00:00', EzChar.Z)
            : isoDateTimeWithUTCTimeZone;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIsoOrDefault(momentInstance, timeZoneId, defaultValue)
     */
    ezToIsoOrDefault = EzDateTime.toIsoOrDefault;

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * The moment to convert to an ISO 8601 string while keeping its own time zone.
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZone(momentInstance) {
        return EzDateTime.ezInstance.ezToIsoMaintainZoneOrDefault(
            momentInstance,
            EzString.EMPTY);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * The moment to format as ISO 8601 in its own time zone; when invalid, defaultValue is returned instead.
     * @param {undefined|null|string} defaultValue
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZoneOrDefault(momentInstance, defaultValue = globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()) {
        let result;

        if (!EzDateTime.isValidMoment(momentInstance)) {
            result = EzString.stringWithLengthOrDefault(
                defaultValue,
                globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
        } else {
            try {
                result = momentInstance.format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT);
            } catch (err) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    `Unable to convert the provided momentInstance to an ISO string due to the following error: ${err.message}`,
                    err);

                result = EzString.stringWithLengthOrDefault(
                    defaultValue,
                    globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
            }
        }

        if (result.includes('+00:00')) {
            result = result.replace('+00:00', EzChar.Z);
        }

        return result;
    }

    /**
     * @public
     * @function
     * @description
     * Generates a default time iso from EzDateTime.now()
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: False
     * @param {boolean} noSeconds
     * Default: True
     * @param {boolean} noMilliseconds
     * Default: True
     * @returns {string}
     * The current time in timeZoneId formatted as 'HH:mm[Z]' or 'HH:mm:ss[Z]' per the flags; undefined if noSeconds and noMilliseconds are false.
     */
    static generateDefaultTimeIso(timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        const nowDateTime = EzDateTime.now();

        let defaultTimeIso;

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ss')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ssZ');
        }

        return defaultTimeIso;
    }

    /**
     * @public
     * @function
     * @description
     * Generates a default time iso from EzDateTime.now()
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: False
     * @param {boolean} noSeconds
     * Default: True
     * @param {boolean} noMilliseconds
     * Default: True
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.generateDefaultTimeIso(timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true)
     */
    ezGenerateDefaultTimeIso = EzDateTime.generateDefaultTimeIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * The moment whose time is formatted as an ISO time string; when invalid, the current time is used instead.
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    static toTimeIso(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        return EzDateTime.toTimeIsoOrDefault(
            momentInstance,
            timeZoneId,
            noZone,
            noSeconds,
            noMilliseconds,
            EzDateTime.generateDefaultTimeIso(
                timeZoneId,
                noZone,
                noSeconds,
                noMilliseconds));
    }

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeIso(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true)
     */
    ezToTimeIso = EzDateTime.toTimeIso;

    /**
     * @public
     * @function
     * @description
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * The moment whose time is formatted as an ISO time string; when invalid, defaultTimeIso (or the current time) is returned.
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @param {undefined|null|string} defaultTimeIso
     * Default: Now time formatted as: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    static toTimeIsoOrDefault(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true,
        defaultTimeIso = EzString.EMPTY) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.hasLength(defaultTimeIso)
                ? defaultTimeIso
                : EzDateTime.generateDefaultTimeIso(
                    timeZoneId,
                    noZone,
                    noSeconds,
                    noMilliseconds);
        }

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ss')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ssZ');
        }

        return EzBoolean.booleanOrFalse(noZone)
            ? momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSS')
            : momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSSZ');
    }

    /**
     * @static
     * @public
     * @function
=======
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @param {undefined|null|string} defaultTimeIso
     * Default: Now time formatted as: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @deprecated
     * Migrate to static method: EzDateTime.toTimeIsoOrDefault(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true,
     *         defaultTimeIso = EzString.EMPTY)
     */
    ezToTimeIsoOrDefault = EzDateTime.toTimeIsoOrDefault

    /**
     * @public
     * @function
>>>>>>> refs/heads/OpenJDK11/Spring4/Hibernate4/merge/develop/releases/r80.0.x
     * Returns the moment as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * The moment to convert to a UNIX millisecond timestamp.
     * @returns {string}
     * The UNIX millisecond timestamp (moment.js format 'x') as a string, or an empty string if momentInstance is invalid.
     */
    static toTimestamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format(EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the moment as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to to static function: EzDateTime.toTimestamp(momentInstance)
     */
    ezToTimestamp = EzDateTime.toTimeStamp;

    /**
     * @public
     * @function
     * @description
     * Returns the provided date as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @param {moment} momentInstance
     * The moment to convert to a version stamp.
     * @returns {string}
     * The momentInstance in the preferred time zone formatted as 'YYYYMMDDHHmmssSSS', or an empty string if momentInstance is invalid.
     */
    ezToVersionStamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns the current date and time as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @returns {string}
     * The current date and time formatted as 'YYYYMMDDHHmmssSSS', e.g. 20240305083000123.
     */
    static get nowVersionStamp() {
        return EzDateTime.now().format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @public
     * @readonly
     * @description
     * Returns the current date and time as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @returns {string}
     * The current date and time formatted as 'YYYYMMDDHHmmssSSS', e.g. 20240305083000123.
     * @deprecated
     * Migrate to static property: EzDateTime.nowVersionStamp()
     */
    get ezNowVersionStamp() {
        return EzDateTime.nowVersionStamp;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the moment.now() as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * The current date and time in timeZoneId as a UNIX millisecond timestamp string (moment.js format 'x').
     */
    static nowAsTimestamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(
            EzString.stringWithTextOrDefault(timeZoneId, EzDateTime.ezActiveTimeZoneId))
            .format(EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the moment.now() as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.nowAsTimestamp(timeZoneId)
     */
    ezNowAsTimestamp = EzDateTime.nowAsTimestamp;

    /**
     * @public
     * @function
     * @description
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * The moment to convert to a UTC ISO 8601 string.
     * @returns {string}
     * The ISO 8601 string from EzDateTime.toIso(EzDateTime.withZoneUTC(momentInstance)), or an empty string if momentInstance is invalid.
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezToIsoUTC(momentInstance)
     */
    ezToUTCIsoDateTime(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.toIso(
            EzDateTime.withZoneUTC(momentInstance));
    }

    /**
     * @public
     * @function
     * @description
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * The moment to convert to the UTC time zone.
     * @returns {moment}
     * The momentInstance converted to the UTC time zone.
     */
    static withZoneUTC(momentInstance) {
        return EzDateTime.withZone(momentInstance, EzDateTime.timeZoneIds.UTC);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToIsoUTC = EzDateTime.withZoneUTC;

    /**
     * @public
     * @function
     * @description
     * Returns a moment instance in the preferred timezone for the date, with time at the beginning of the date.
     * @param {number} aMonth
     * From 0 to 11
     * @param {number} aDay
     * From 1 to 31
     * @param {number} aYear
     * The full four digit year, e.g. 2024.
     * @returns {moment}
     * A moment in the active time zone for the provided month, day, and year with the time at 00:00:00.000.
     */
    ezFromDate(aMonth, aDay, aYear) {
        if (!EzNumber.isNumber(aMonth)) {
            throw new EzBadParamException(
                'aMonth',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aDay)) {
            throw new EzBadParamException(
                'aDay',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aYear)) {
            throw new EzBadParamException(
                'aYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }

        return EzDateTime.now()
            .set('year', aYear)
            .set('date', aDay)
            .set('month', aMonth)
            .set('hour', EzNumber.ZERO)
            .set('minute', EzNumber.ZERO)
            .set('second', EzNumber.ZERO)
            .set('millisecond', EzNumber.ZERO);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the moment from the provided date string in format mm/dd/yyyy
     * @param {string} dateString
     * The forward slash delimited date string to parse, in MM/DD/YYYY format.
     * @returns {moment}
     * A moment for the parsed date with the time at 00:00:00.000 (see ezFromDate).
     */
    ezFromDateString(dateString) {
        if (!EzString.hasLength(dateString)) {
            throw new EzBadParamException(
                'dateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDateString);
        }

        const dateParts = dateString.split(EzChar.FORWARD_SLASH);

        // month is zero based
        return EzDateTime.ezInstance.ezFromDate(
            EzNumber.strToInt(dateParts[EzNumber.FIRST_INDEX]) - EzNumber.ONE,
            EzNumber.strToInt(dateParts[EzNumber.SECOND_INDEX]),
            EzNumber.strToInt(dateParts[EzNumber.THIRD_INDEX]));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a moment date time instance from the provided dateString that is expected to be in either the provided dateStringFormat OR
     * the EzDateTime.ezPreferredDateFormat.
     * Optionally sets the moment instance's time zone to the provided timeZoneId
     * @param {string} dateString
     * IMPORTANT: The provided dateString must match the provided format or EzDateTimeezPerferredDateForm string.
     * Default: null
     * @param {undefined|null|string} dateFormatString
     * Default: EzDateTime.ezPreferredDateFormat
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId parsed from dateString using dateFormatString (or EzDateTime.ezPreferredDateFormat).
     */
    static dateTimeFromDateStringInFormat(dateString = null, dateFormatString = EzDateTime.ezPreferredDateFormat, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(dateString)) {
            throw new EzBadParamException(
                { dateString },
                EzDateTime,
                EzDateTime.dateTimeFromDateStringInFormat);
        }

        if (!EzString.hasText(dateFormatString)) {
            dateFormatString = EzDateTime.ezPreferredDateFormat;
        }

        return moment.tz(
            EzDateTime.patchBadDecodedIso(dateString),
            EzString.stringWithTextOrDefault(
                dateFormatString,
                EzDateTime.ezPreferredDateFormat),
            EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment date time instance from the provided dateString that is expected to be in either the provided dateStringFormat OR
     * the EzDateTime.ezPreferredDateFormat.
     * Optionally sets the moment instance's time zone to the provided timeZoneId
     * @param {string} dateString
     * IMPORTANT: The provided dateString must match the provided format or EzDateTimeezPerferredDateForm string.
     * Default: null
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeFromDateStringInFormat(dateString, dateFormatString, timeZoneId)
     */
    ezMomentFromDateStringAllFormats = EzDateTime.dateTimeFromDateStringInFormat;

    /**
     * @public
     * @function
     * @description
     * Returns the moment from the provided date string.
     * String is expected to be in the format of the  EzDateTime.ezInstance.ezPreferredDateFormat result
     * @param {string} preferredDateString
     * The date string to parse strictly with EzDateTime.ezPreferredDateFormat.
     * @returns {moment}
     * A moment in the preferred time zone parsed from preferredDateString.
     */
    ezMomentFromPreferredDateString(preferredDateString) {
        if (!EzString.hasLength(preferredDateString)) {
            throw new EzBadParamException(
                'preferredDateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPreferredDateString);
        }

        const momentInstance = moment(
            preferredDateString,
            EzDateTime.ezPreferredDateFormat,
            true);

        if (!momentInstance.isValid()) {
            throw new EzException(
                EzString.em`
                    The provided preferredDateString value of "${preferredDateString}" did not translate to a momentjs instance.
                    Assumed formatting of ${EzDateTime.ezPreferredDateFormat}`);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the preferred timezone moment instance if aValue is valid,
     * or the default timezone instance if aValue is not valid.
     * @param {string} aValue
     * The date time string to parse with moment().
     * @returns {moment}
     * A moment in the preferred time zone for aValue, or today with the time at start of day if aValue is not a valid date.
     */
    ezCreateFromValue(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                'aValue',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromValue);
        }

        const momentInstance = !moment(aValue);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @public
     * @function
     * @param {string} dateTimeString
     * The date time string to parse.
     * @param {string} expectedFormat
     * The moment.js format string that dateTimeString is expected to match.
     * @returns {moment}
     * A moment in the preferred time zone parsed from dateTimeString, today at start of day if invalid, or null if an argument is empty.
     */
    ezCreateFromValueInFormat(dateTimeString, expectedFormat) {
        if (!EzString.hasLength(dateTimeString) || !EzString.hasLength(expectedFormat)) {
            return null;
        }

        const momentInstance = moment(dateTimeString, expectedFormat);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * The current date and time in timeZoneId as an ISO 8601 string.
     */
    static nowAsIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.now(
                EzDateTime.safeTimeZoneId(timeZoneId)));
    }

    /**
     * @public
     * @function
     * @description
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    ezNowAsIso = EzDateTime.nowAsIso;

    /**
     * @static
     * @public
     * @function
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment for the current date and time in timeZoneId (falls back to the active or default time zone on error).
     */
    static now(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        try {
            return moment().tz(EzDateTime.safeTimeZoneId(timeZoneId));
        } catch (err) {
            if (!EzString.hasText(err?.message)) {
                err.message = `Unexpected error when creating a new date time instance (moment) with timeZoneId=${timeZoneId}.`;
            }

            globalThis?.console?.error(
                EzString.em`
                    Failed to get date time instance for now with timeZoneId=${timeZoneId}
                    due to the following error: ${err.message}`,
                err);

            // Re-try by using the active time zone id:
            try {
                return moment.tz(
                    EzDateTime.safeTimeZoneId(EzDateTime.ezActiveTimeZoneId));
            } catch (err2) {
                globalThis?.console?.error(
                    EzString.em`
                        Failed to get date time instance for now with timeZoneId=${timeZoneId}
                        due to the following error: ${err.message}`,
                    err2);

                // Last try using what ever the default time zone id:
                return moment();
            }
        }
    }

    /**
     * @public
     * @function
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.now(timeZoneId)
     */
    ezNow = EzDateTime.now;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * A moment for today in timeZoneId with the time at 00:00:00.000.
     */
    static nowWithStartOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.now(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDay(timeZoneId)
     */
    ezNowWithStartOfDay = EzDateTime.nowWithStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {string}
     * Today's date in timeZoneId with the time at 00:00:00.000 as an ISO 8601 string.
     */
    static nowWithStartOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithStartOfDay(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithStartOfDayIso = EzDateTime.nowWithStartOfDayIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * A moment for today in timeZoneId with the time at 23:59:59.999.
     */
    static nowWithEndOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).endOf('day');
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithEndOfDay(timeZoneId)
     */
    ezNowWithEndOfDay = EzDateTime.nowWithEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {string}
     * Today's date in timeZoneId with the time at 23:59:59.999 as an ISO 8601 string.
     */
    static nowWithEndOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithEndOfDay(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithEndOfDayIso = EzDateTime.nowWithEndOfDayIso;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * The moment whose month's start is returned.
     * @returns {moment}
     * A moment in the preferred time zone at the start of momentInstance's month (day 1 at 00:00:00.000).
     */
    static momentAtStartOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime,
                EzDateTime.momentAtStartOfMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance).startOf('month');
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentAtStartOfMonth(momentInstance)
     */
    ezMomentAtStartOfMonth = EzDateTime.momentAtStartOfMonth;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * The moment whose month's end is returned.
     * @returns {moment}
     * A moment in the preferred time zone at the end of momentInstance's month (last day at 23:59:59.999).
     */
    static momentAtEndOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime.ezInstance,
                EzDateTime.momentAtEndOfMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .endOf('month');
    }

    /**
     * @public
     * @function
     * @description
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentAtEndOfMonth(momentInstance)
     */
    ezMomentAtEndOfMonth = EzDateTime.momentAtEndOfMonth;

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the beginning of the day (00:00:00.000)
     * @param {moment} momentInstance
     * The moment whose year is used.
     * @param {string} timeZoneId
     * The time zone id to use for the new moment, e.g. 'America/Chicago'.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId with the time at 00:00:00.000, intended to be January 1st of momentInstance's year.
     */
    ezSetMomentFirstDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentFirstDayOfYear);
        }

        return EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId).date(1).day(1);
    }

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the end of the day (23:59:59.999)
     * @param {moment} momentInstance
     * The moment whose year is used.
     * @param {string} timeZoneId
     * The time zone id to use for the new moment, e.g. 'America/Chicago'.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId with the time at 23:59:59.999, intended to be the last day of momentInstance's year.
     */
    ezSetMomentLastDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentLastDayOfYear);
        }

        return EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId).date(12).day(31);
    }


    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|unknown|moment} momentInstance
     * Default: null
     * @returns {boolean}
     * True if momentInstance is a valid moment.js instance; otherwise, false.
     */
    static isValidMoment(momentInstance = null) {
        return EzObject.isValid(momentInstance) && momentInstance?._isAMomentObject && momentInstance?._isValid;
    }

    /**
     * @public
     * @function
     * @description
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|unknown|moment} momentInstance
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to the static method: EzDateTime.isValidMoment(momentInstance = null)
     */
    ezIsValidMoment = EzDateTime.isValidMoment;

    /**
     * @static
     * @public
     * @function
     * @description
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then the provided defaultMoment is returned based upon the following logic:
     *   2a) If the defaultMoment is a valid moment instance:
     *      a) If the timeZoneId is provided, then the defaultMoment is returned in the provided timeZoneId.
     *      b) If a timeZoneId is not provided, the the defaultMoment is returned as it was provided (does not apply the deafult timeZoneId)
     * 3) If the defaultMoment is not a valid moment instance, then the defaultMoment is returned as it was provided
     * @param {undefined|null|moment} [momentInstance]
     * Default: null
     * @param {undefined|null|moment|unknown} [defaultMoment]
     * Default: EzDateTime.now()
     * @param {undefined|null|string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {undefined|null|moment|unknown}
     * momentInstance in timeZoneId if valid; otherwise defaultMoment (converted to timeZoneId when it is a valid moment and timeZoneId has text).
     */
    static dateTimeOrDefault(momentInstance, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(momentInstance, timeZoneId)
            : EzDateTime.isValidMoment(defaultMoment)
                ? EzString.hasText(timeZoneId)
                    ? EzDateTime.withZone(defaultMoment, timeZoneId)
                    : defaultMoment
                : defaultMoment;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then null is returned.
     * @param {undefined|null|moment} [momentInstance]
     * Default: null
     * @param {undefined|null|string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {null|moment}
     * momentInstance converted to timeZoneId if it is a valid moment; otherwise, null.
     */
    static dateTimeOrNull(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.dateTimeOrDefault(
            momentInstance,
            null,
            timeZoneId);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * 1) If the provided momentInstance is a valid moment instance, then the momentInstance is returned transformed to the provided timeZoneId or
     * the default time zone id.
     * 2) If the provided momentInstance is not a valid moment instance, then EzDateTime.now() is returned transformed to the provided timeZoneId or
     * the EzDateTime.ezActiveTimeZoneId.
     * @param {undefined|null|moment} [momentInstance]
     * Default: null
     * @param {undefined|null|string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * momentInstance (converted to the active time zone) if it is a valid moment; otherwise, the current date and time in timeZoneId.
     */
    static dateTimeOrNow(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.dateTimeOrDefault(
            momentInstance,
            EzDateTime.now(timeZoneId));
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     */
    ezDateTimeOrNow = EzDateTime.dateTimeOrNow;

    /**
     * @public
     * @function
     * @description
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.dateTimeOrNow(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     */
    ezAssignMomentOrNow = EzDateTime.dateTimeOrNow;

    /**
     * @public
     * @function
     * @description
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * The moment whose date is used; when invalid, the current date is used instead.
     * @param {undefined|null|string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A moment in timeZoneId for momentInstance's date (or today) with the time at 00:00:00.000.
     */
    ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.dateTimeOrNow(momentInstance, timeZoneId), timeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * 1) From within the EzDateTime class:
     *      EzDateTime.ezInstance.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId)
     * 2) From outside the EzDateTime class:
     *      globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId);
     */
    ezAssignMomentOrNowThenSetTimeAtStartOfDay = this.ezDateTimeOrNowWithTimeAtStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @param {undefined|null|moment} overrideStartMoment
     * Optional moment to use as the period start date (time set to start of day) instead of today.
     * Default: null
     * @param {undefined|null|moment} overrideEndMoment
     * Optional moment to use as the period end date (time set to end of day) instead of the start date plus 13 days.
     * Default: null
     * @returns {object}
     * The selected period object from EzDateTime.createSelectedPeriod (start/end moments and ISO strings in the active and UTC time zones).
     */
    static createDefaultSelectedPeriod(overrideStartMoment = null, overrideEndMoment = null) {
        const nowEzDateTimeWithTimeAtStartOfDayActive = EzDateTime.isValidMoment(overrideStartMoment)
            ? EzDateTime.withTimeAtStartOfDay(overrideStartMoment)
            : EzDateTime.nowWithStartOfDay();

        return EzDateTime.createSelectedPeriod(
            nowEzDateTimeWithTimeAtStartOfDayActive,
            EzDateTime.withTimeAtEndOfDay(
                EzDateTime.isValidMoment(overrideEndMoment)
                    ? overrideEndMoment
                    : EzDateTime.addDays(
                        nowEzDateTimeWithTimeAtStartOfDayActive,
                        EzNumber.THIRTEEN)));
    }

    /**
     * @public
     * @function
     * @description
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @returns {object}
     * @deprecated
     * Migreate to static method: EzDateTime.createDefaultSelectedPeriod()
     */
    ezCreateDefaultSelectedPeriod = EzDateTime.createDefaultSelectedPeriod;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     * A JSON string of the default selected period object, with ezPeriodStartMoment and ezPeriodEndMoment converted to ISO 8601 strings.
     */
    static createDefaultSelectedPeriodJson() {
        const selectedPeriod = EzDateTime.createDefaultSelectedPeriod();

        selectedPeriod.ezPeriodStartMoment = EzDateTime.toIso(selectedPeriod.ezPeriodStartMoment);

        selectedPeriod.ezPeriodEndMoment = EzDateTime.toIso(selectedPeriod.ezPeriodEndMoment);

        return EzJson.toJson(selectedPeriod);
    }

    /**
     * @public
     * @function
     * @description
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     * @deprecated
     * Migreate to static method:  EzDateTime.createDefaultSelectedPeriodJso()
     */
    ezCreateDefaultSelectedPeriodJson = EzDateTime.createDefaultSelectedPeriodJson;

    /**
     * Transforms the provided momentInYear to the last date time of the year the
     * provided momentInYear is for.
     * @param {moment} momentInYear
     * The moment instance whose year is used to calculate the end of the year.
     * @returns {moment}
     * A new moment in the active time zone set to the last moment (23:59:59.999 on December 31) of momentInYear's year.
     */
    ezToEndOfYear(momentInYear) {
        if (!EzDateTime.isValidMoment(momentInYear)) {
            throw new EzBadParamException(
                'momentInYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToEndOfYear);
        }

        return EzDateTime.momentWithPTZ(momentInYear)
            .endOf('year');
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provied momentInstance as a display string formatted
     * for the date input controls.
     * @param {moment} momentInstance
     * The moment instance to format for display in a date picker input.
     * @returns {string}
     * The momentInstance in the active time zone formatted with EzDateTime.DEFAULT_MM_DD_YYYY_FORMAT (e.g. 01/31/2026).
     */
    ezToDatePickerDisplayDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDatePickerDisplayDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MM_DD_YYYY_FORMAT);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * The first day of the period; the period start is set to the start of this day.
     * @param {moment} periodEndMoment
     * The last day of the period; the period end is set to the end of this day.
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     */
    static createSelectedPeriod(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                { periodStartMoment },
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                { periodEndMoment },
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }

        const startMomentActive = EzDateTime.withTimeAtStartOfDay(periodStartMoment);

        const endMomentActive = EzDateTime.withTimeAtEndOfDay(periodEndMoment);

        const startMomentUTC = EzDateTime.withTimeAtStartOfDay(
            periodStartMoment,
            EzTimeZone.UTC);

        const endMomentUTC = EzDateTime.withTimeAtEndOfDay(
            periodEndMoment,
            EzTimeZone.UTC);

        const startIsoActive = EzDateTime.toIso(startMomentActive);

        const endIsoActive = EzDateTime.toIso(endMomentActive);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        /* TODO: Use the new EzTimePeriod class instead of returning an object with the period values.
        return new EzTimePeriod(
            startMomentActive,
            endMomentActive,
            EzDateTime.ezActiveTimeZoneId);
         */

        return {
            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Start Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Preferred reference period start date time with primary/active time zone
             * @type {moment}
             */
            ezPeriodStartMoment: startMomentActive,

            /**
             * Preferred reference of period start ISO with primary/active time zone
             * @type {string}
             */
            ezStartIsoActive: startIsoActive,

            /**
             * Preferred reference of period start date time reference with UTC time zone
             * @type {moment}
             */
            ezPeriodStartMomentUTC: startMomentUTC,

            /**
             * Preferred reference of period start ISO with UTC time zone
             * @type {string}
             */
            ezStartIsoUTC: startIsoUTC,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * End Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Preferred reference period end date time with primary/active time zone
             * @type {moment}
             */
            ezPeriodEndMoment: endMomentActive,

            /**
             * Preferred reference of period end ISO with primary/active time zone
             * @type {string}
             */
            ezEndIsoActive: endIsoActive,

            /**
             * Preferred reference of period end date time reference with UTC time zone
             * @type {moment}
             */
            ezPeriodEndMomentUTC: endMomentUTC,

            /**
             * Preferred reference of period end ISO with UTC time zone
             * @type {string}
             */
            ezEndIsoUTC: endIsoUTC,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Legacy Start Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */

            /**
             * Alias of property ezPeriodStartMoment
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezPeriodStartMoment
             */
            ezStartEzDateTimeActive: startMomentActive,

            /**
             * Alias of property ezPeriodStartMomentUTC
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezPeriodStartMomentUTC
             */
            ezStartEzDateTimeUTC: startMomentUTC,

            /**
             * Alias for property ezPeriodStartMoment
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezPeriodStartMoment
             */
            periodStartMoment: startMomentActive,

            /**
             * Alias for property ezStartIsoActive
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezStartIsoActive
             */
            periodStartIso: startIsoActive,

            /*
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             * Legacy End Period Date Time Values
             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
             */
            /**
             * Alias for property ezPeriodEndMoment
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezStartIsoActive
             */
            ezEndEzDateTimeActive: endMomentActive,

            /**
             * Alias for property ezPeriodEndMomentUTC
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezPeriodEndMomentUTC
             */
            ezEndEzDateTimeUTC: endMomentUTC,

            /**
             * Alias for property ezPeriodEndMoment
             * @type {moment}
             * @deprecated
             * Stop all use and migrate to property ezPeriodEndMoment
             */
            periodEndMoment: endMomentActive,

            /**
             * Alias for property ezEndIsoActive
             * @type {string}
             * @deprecated
             * Stop all use and migrate to property ezEndIsoActive
             */
            periodEndIso: endIsoActive
        };
    }

    /**
     * @public
     * @function
     * @description
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     * @deprecated
     * Migrate to static method: EzDateTime.createSelectedPeriod()
     */
    ezCreateSelectedPeriod = EzDateTime.createSelectedPeriod;

    /**
     * @public
     * @function
     * @description
     * Creates a selected period JSON Object from the provided periodStartMoment and periodEndMoment
     * @param {moment} periodStartMoment
     * The moment instance for the start of the period.
     * @param {moment} periodEndMoment
     * The moment instance for the end of the period.
     * @returns {object}
     * A selected period object whose start and end values (active and UTC time zones, plus legacy properties) are ISO 8601 strings.
     */
    ezCreateSelectedPeriodJSON(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                'periodEndMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }

        const startMomentPTZ = EzDateTime.momentWithPTZ(periodStartMoment);

        const startMomentUTC = globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeWithTimeZone(
            periodStartMoment,
            EzTimeZone.UTC);

        const startIsoPTZ = EzDateTime.toIso(startMomentPTZ);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endMomentPTZ = EzDateTime.momentWithPTZ(periodEndMoment);

        const endMomentUTC = EzDateTime.momentWithPTZ(periodEndMoment);

        const endIsoPTC = EzDateTime.toIso(endMomentPTZ);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        // TODO: Create an EzTimePeriod JS class
        return {
            // ezStartEzDateTimeActive: startMomentActive,
            // ezStartEzDateTimeUTC: startMomentUTC,
            ezStartIsoActive: startIsoPTZ,
            ezStartIsoUTC: startIsoUTC,
            // ezEndEzDateTimeActive: endEzDateTimeActive,
            // ezEndEzDateTimeUTC: endEzDateTimeUTC,
            ezEndIsoActive: endIsoPTC,
            ezEndIsoUTC: endIsoUTC,
            // Legacy properties - migrate to one of the above
            // ezPeriodStartMoment: startMomentActive,
            // periodStartMoment: startMomentActive,
            // ezPeriodEndMoment: endEzDateTimeActive,
            // periodEndMoment: endEzDateTimeActive,
            periodStartIso: startIsoPTZ,
            periodEndIso: endIsoPTC
        };
    }

    /**
     * @public
     * @function
     * @description
     * Creates a selected period object from the provided moments, sets the values as ISO strings, converts to JSON
     * @param {moment} periodStartMoment
     * The first day of the period; the period start is set to the start of this day.
     * @param {moment} periodEndMoment
     * The last day of the period; the period end is set to the end of this day.
     * @returns {string}
     * The JSON string of the selected period object created by EzDateTime.createSelectedPeriod(periodStartMoment, periodEndMoment).
     */
    ezCreateSelectedPeriodJson(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                periodStartMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                periodEndMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }

        return EzJson.toJson(
            EzDateTime.createSelectedPeriod(periodStartMoment, periodEndMoment));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * Documentation: https://momentjs.com/docs/#/manipulating/
     * Calls momentjs api method: moment.add(amountToAdd, fieldName)
     * @param {moment} momentInstance
     * The moment instance to add to; a copy converted to the active time zone is modified.
     * @param {string} fieldName
     * Supported field names:
     *  |----------------|-----------|
     *  | Field Name     | Shorthand |
     *  |----------------|-----------|
     *  | years          | y         |
     *  | quarters       | Q         |
     *  | months         | M         |
     *  | weeks          | w         |
     *  | days           | d         |
     *  | hours          | h         |
     *  | minutes        | m         |
     *  | seconds        | s         |
     *  | milliseconds   | ms        |
     *  |----------------|-----------|
     * @param {number} amountToAdd
     * The amount of the fieldName unit to add (may be negative).
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus amountToAdd of the fieldName unit.
     */
    static add(momentInstance, fieldName, amountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzString.hasText(fieldName)) {
            throw new EzBadParamException(
                'fieldName',
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzNumber.isNumber(amountToAdd)) {
            throw new EzBadParamException(
                'amountToAdd',
                EzDateTime,
                EzDateTime.add);
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.add(amountToAdd, fieldName);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Adds the provided amountOfWeeksToAdd weeks to the provided momentInstance and returns the new moment instance.
     * @param {moment} momentInstance
     * The moment instance to add the weeks to.
     * @param {number} [amountOfWeeksToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus amountOfWeeksToAdd weeks.
     */
    static addWeeks(momentInstance, amountOfWeeksToAdd = EzNumber.ZERO) {
        return EzDateTime.add(momentInstance, 'weeks', amountOfWeeksToAdd);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.add(momentInstance, fieldName, amountToAdd)
     */
    ezPlus = EzDateTime.add;

    /**
     * @public
     * @function
     * @description
     * Sets the momentInstance instance to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.add(momentInstance, fieldName, amountToAdd)
     */
    ezAdd = EzDateTime.add;

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add to.
     * @param {undefined|null|string} [aValueKey]
     * Default: 'minutes'
     * One of the following string values: 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'
     * or their shorthand values: EzChar.y, EzChar.M, EzChar.w, EzChar.d, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {undefined|null|number} [aAmountToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus aAmountToAdd of the aValueKey unit.
     */
    static addUTC(momentInstance, aValueKey = 'minutes', aAmountToAdd = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAddUTC);
        }

        return EzDateTime.add(
            EzDateTime.ezInstance.ezToUTC(momentInstance),
            EzString.stringWithTextOrDefault(aValueKey, 'minutes'),
            EzNumber.numberOrZero(aAmountToAdd));
    }

    /**
     * @public
     * @function
     * @description
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {undefined|null|string} [aValueKey]
     * Default: 'minutes'
     * One of the following string values: 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'
     * or their shorthand values: EzChar.y, EzChar.M, EzChar.w, EzChar.d, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {undefined|null|number} [aAmountToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     */
    ezPlusUTC

    /**
     * @public
     * @function
     * @description
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add to.
     * @param {string} aValueKey
     * The unit of time to add (full name or shorthand), e.g. 'days' or 'd'.
     * @param {number} aAmountToAdd
     * The amount of the aValueKey unit to add.
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus aAmountToAdd of the aValueKey unit.
     */
    ezAddUTC(momentInstance, aValueKey, aAmountToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, aValueKey, aAmountToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'years', yearsToAdd)
     * @param {moment} momentInstance
     * The moment instance to add the years to.
     * @param {number} yearsToAdd
     * The number of years to add.
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus yearsToAdd years.
     */
    ezPlusYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.add(momentInstance, 'years', yearsToAdd);
     * @param {moment} momentInstance
     * The moment instance to add the years to.
     * @param {number} yearsToAdd
     * The number of years to add.
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus yearsToAdd years.
     */
    static addYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.add(momentInstance, 'years', yearsToAdd);
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addYears(momentInstance, yearsToAdd)
     */
    ezAddYears = EzDateTime.addYears;

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * The moment instance to add the months to.
     * @param {number} monthsToAdd
     * The number of months to add.
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus monthsToAdd months.
     */
    static addMonths(momentInstance, monthsToAdd) {
        return EzDateTime.add(momentInstance, 'months', monthsToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addMonths(momentInstance, monthsToAdd)
     */
    ezAddMonths = EzDateTime.addMonths;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.addMonths(momentInstance, monthsToAdd)
     */
    ezPlusMonths = EzDateTime.addMonths;

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * The moment instance to add the days to.
     * @param {number} daysToAdd
     * The number of days to add.
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus daysToAdd days.
     */
    static addDays(momentInstance, daysToAdd) {
        return EzDateTime.add(momentInstance, 'days', daysToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezPlusDays = EzDateTime.addDays;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezAddDays = EzDateTime.addDays;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment equal to the provided momentInstance plus the provided hoursToAdd.
     * @param {moment} momentInstance
     * The moment instance to add the hours to.
     * @param {number} [hoursToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus hoursToAdd hours.
     */
    static addHours(momentInstance, hoursToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToAdd));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezPlusHours = EzDateTime.addHours;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezAddHours = EzDateTime.addHours;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment equal to the provided momentInstance plus the provided minutesToAdd.
     * @param {moment} momentInstance
     * The moment instance to add the minutes to.
     * @param {number} [minutesToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus minutesToAdd minutes.
     */
    static addMinutes(momentInstance, minutesToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToAdd));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezPlusMinutes = EzDateTime.addMinutes;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezAddMinutes = EzDateTime.addMinutes;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment equal to the provided momentInstance plus the provided secondsToAdd.
     * @param {moment} [momentInstance]
     * Default: null
     * @param {number} [secondsToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus secondsToAdd seconds.
     */
    static addSeconds(momentInstance, secondsToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'seconds',
            EzNumber.numberOrZero(secondsToAdd));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a moment equal to the provided momentInstance plus the provided millisecondsToAdd.
     * @param {moment} [momentInstance]
     * Default: null
     * @param {number} [millisecondsToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance plus millisecondsToAdd milliseconds.
     */
    static addMilliseconds(momentInstance, millisecondsToAdd = EzNumber.ZERO) {
        return EzDateTime.add(
            momentInstance,
            'milliseconds',
            EzNumber.numberOrZero(millisecondsToAdd));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Alias for: EzDateTime.ezInstance.ezAddDaysUTC(momentInstance, daysToAdd)
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the days to.
     * @param {number} [daysToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus daysToAdd days.
     */
    ezPlusDaysUTC(momentInstance, daysToAdd = EzNumber.ZERO) {
        return EzDateTime.ezInstance.ezAddDaysUTC(momentInstance, daysToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'days', daysToAdd);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the days to.
     * @param {number} [daysToAdd]
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus daysToAdd days.
     */
    ezAddDaysUTC(momentInstance, daysToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'days',
            EzNumber.numberOrZero(daysToAdd));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Alias for: EzDateTime.ezInstance.ezAddHoursUTC(momentInstance, hoursToAdd)
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the hours to.
     * @param {number} hoursToAdd
     * The number of hours to add.
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus hoursToAdd hours.
     */
    ezPlusHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezAddHoursUTC(momentInstance, hoursToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the hours to.
     * @param {number} hoursToAdd
     * The number of hours to add (non-number values are treated as 0).
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus hoursToAdd hours.
     */
    ezAddHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToAdd));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Alias for: EzDateTime.ezInstance.ezAddMinutesUTC(momentInstance, minutesToAdd)
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the minutes to.
     * @param {number} minutesToAdd
     * The number of minutes to add (non-number values are treated as 0).
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus minutesToAdd minutes.
     */
    ezPlusMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezAddMinutesUTC(momentInstance, minutesToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and add the minutes to.
     * @param {number} minutesToAdd
     * The number of minutes to add (non-number values are treated as 0).
     * @returns {moment}
     * A new UTC moment equal to momentInstance plus minutesToAdd minutes.
     */
    ezAddMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToAdd));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * The moment instance to subtract from; a copy converted to the active time zone is modified.
     * @param {string} aValueKey
     * One of the following: 'years', 'days', 'weeks', 'months', 'hours', 'minutes', 'seconds', 'milliseconds' or
     * their shorthand values: EzChar.y, EzChar.d, EzChar.w, EzChar.M, EzChar.h, EzChar.m, EzChar.s, 'ms'
     * @param {number} [aAmountToSubtract]
     * Default: 0
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus aAmountToSubtract of the aValueKey unit.
     */
    static minus(momentInstance, aValueKey, aAmountToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minus);
        }
        if (!EzString.hasText(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime,
                EzDateTime.minus);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .subtract(
                EzNumber.numberOrZero(aAmountToSubtract),
                aValueKey);
    }

    /**
     * @public
     * @function
     * @description
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToSubtract
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minus(momentInstance, aValueKey, aAmountToSubtract)
     */
    ezMinus = EzDateTime.minus;

    /**
     * @public
     * @function
     * @description
     * Sets the momentInstance instnace to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and subtract from.
     * @param {string} aValueKey
     * The unit of time to subtract (full name or shorthand), e.g. 'days' or 'd'.
     * @param {number} aAmountToAdd
     * The amount of the aValueKey unit to subtract.
     * @returns {moment}
     * A UTC moment equal to momentInstance minus aAmountToAdd of the aValueKey unit.
     */
    ezMinusUTC(momentInstance, aValueKey, aAmountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzString.hasLength(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzNumber.isNumber(aAmountToAdd)) {
            throw new EzBadParamException(
                'aAmountToAdd',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        return EzDateTime.ezInstance.ezToUTC(momentInstance).subtract(aValueKey, aAmountToAdd);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided yearsToSubtract years.
     * Calls:  EzDateTime.minus(momentInstance, 'years', yearsToSubtract)
     * @param {moment} momentInstance
     * The moment instance to subtract the years from.
     * @param {string|number} yearsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus yearsToSubtract years.
     */
    ezMinusYears(momentInstance, yearsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusYears);
        }
        if (!EzNumber.isNumber(yearsToSubtract)) {
            if (EzString.isString(yearsToSubtract)) {
                yearsToSubtract = EzNumber.strToIntDefault(yearsToSubtract, 0);

                if (EzNumber.isNaN(yearsToSubtract)) {
                    throw new EzBadParamException(
                        'yearsToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusYears);
                }
            } else {
                throw new EzBadParamException(
                    'yearsToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusYears);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'years',
            yearsToSubtract);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided monthsToSubtract months.
     * Calls:  EzDateTime.minus(momentInstance, 'months', monthsToSubtract)
     * @param {moment} momentInstance
     * The moment instance to subtract the months from.
     * @param {string|number} monthsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus monthsToSubtract months.
     */
    static minusMonths(momentInstance, monthsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minusMonths);
        }
        if (!EzNumber.isNumber(monthsToSubtract)) {
            if (EzString.isString(monthsToSubtract)) {
                monthsToSubtract = EzNumber.strToIntDefault(monthsToSubtract, 0);

                if (EzNumber.isNaN(monthsToSubtract)) {
                    throw new EzBadParamException(
                        'monthsToSubtract',
                        EzDateTime,
                        EzDateTime.minusMonths);
                }
            } else {
                throw new EzBadParamException(
                    'monthsToSubtract',
                    EzDateTime,
                    EzDateTime.minusMonths);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'months',
            monthsToSubtract);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided monthsToSubtract months.
     * Calls:  EzDateTime.minus(momentInstance, 'months', monthsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} monthsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minusMonths(momentInstance, monthsToSubtract)
     */
    ezMinusMonths = EzDateTime.minusMonths;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided weeksToSubtract weeks
     * Calls:  EzDateTime.minus(momentInstance, 'weeks', weeksToSubtract)
     * @param {moment} momentInstance
     * The moment instance to subtract the weeks from.
     * @param {string|number} weeksToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus weeksToSubtract weeks.
     */
    ezMinusWeeks(momentInstance, weeksToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusWeeks);
        }
        if (!EzNumber.isNumber(weeksToSubtract)) {
            if (EzString.isString(weeksToSubtract)) {
                weeksToSubtract = EzNumber.strToIntDefault(weeksToSubtract, 0);

                if (EzNumber.isNaN(weeksToSubtract)) {
                    throw new EzBadParamException(
                        'weeksToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusWeeks);
                }
            } else {
                throw new EzBadParamException(
                    'weeksToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusWeeks);
            }
        }

        return EzDateTime.minus(
            momentInstance,
            'weeks',
            weeksToSubtract);
    }


    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided {daysToSubtract} number of days.
     * Calls:  EzDateTime.minus(momentInstance, 'days', daysToSubtract)
     * @param {undefined|null|moment} [momentInstance]
     * Default: null
     * @param {string|number} [daysToSubtract]
     * Default: 0
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus daysToSubtract days.
     */
    static minusDays(momentInstance = null, daysToSubtract = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.minusDays);
        }

        if (!EzNumber.isNumber(daysToSubtract)) {
            daysToSubtract = EzNumber.strToIntDefault(daysToSubtract, EzNumber.ZERO);
        }

        return EzDateTime.minus(
            momentInstance,
            'days',
            daysToSubtract);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided {daysToSubtract} number of days.
     * Calls:  EzDateTime.minus(momentInstance, 'days', daysToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} daysToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.minusDays(momentInstance, daysToSubtract)
     */
    ezMinusDays = EzDateTime.minusDays;

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided hoursToSubtract number of hours.
     * Calls:  EzDateTime.minus(momentInstance, 'hours', hoursToSubtract)
     * @param {moment} momentInstance
     * The moment instance to subtract the hours from.
     * @param {number} hoursToSubtract
     * The number of hours to subtract (non-number values are treated as 0).
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus hoursToSubtract hours.
     */
    ezMinusHours(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHours);
        }

        return EzDateTime.minus(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided minutesToSubtract number of minutes.
     * Calls:  EzDateTime.minus(momentInstance, 'minutes', minutesToSubtract);
     * @param {moment} momentInstance
     * The moment instance to subtract the minutes from.
     * @param {number} minutesToSubtract
     * The number of minutes to subtract (non-number values are treated as 0).
     * @returns {moment}
     * A new moment in the active time zone equal to momentInstance minus minutesToSubtract minutes.
     */
    ezMinusMinutes(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutes);
        }

        return EzDateTime.minus(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then subtracts the provided daysToSubtract number of days.
     * Calls:  EzDateTime.ezInstance.ezMinusUTC(momentInstance, 'days', daysToSubtract);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and subtract the days from.
     * @param {number} daysToSubtract
     * The number of days to subtract (non-number values are treated as 0).
     * @returns {moment}
     * A UTC moment equal to momentInstance minus daysToSubtract days.
     */
    ezMinusDaysUTC(momentInstance, daysToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusDaysUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'days',
            EzNumber.numberOrZero(daysToSubtract));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to UTC time zone then subtracts the provided {hoursToSubtract} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and subtract the hours from.
     * @param {number} hoursToSubtract
     * The number of hours to subtract (non-number values are treated as 0).
     * @returns {moment}
     * A UTC moment equal to momentInstance minus hoursToSubtract hours.
     */
    ezMinusHoursUTC(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHoursUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToSubtract} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * The moment instance to convert to UTC and subtract the minutes from.
     * @param {number} minutesToSubtract
     * The number of minutes to subtract (non-number values are treated as 0).
     * @returns {moment}
     * A UTC moment equal to momentInstance minus minutesToSubtract minutes.
     */
    ezMinusMinutesUTC(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutesUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public
     * @function
     * @description
     * Creates a moment from now with the UTC timezone.
     * @returns {moment}
     * A new moment for the current date and time in the UTC time zone.
     */
    ezNowUTC() {
        return moment().utc();
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * The moment instance to convert to UTC.
     * @returns {moment}
     * A new moment equal to momentInstance in the UTC time zone.
     */
    static toUTC(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.toUTC);
        }

        return moment.utc(momentInstance);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.toUtc(momentInstance)
     */
    ezToUtc = EzDateTime.toUTC;

    /**
     * @public
     * @function
     * @description
     * Returns the moment created from the provided jsDate, transformed to the preferred timezone
     * @param {Date} aJsDate
     * The JavaScript Date object to create the moment from.
     * @returns {moment}
     * A new moment for the same instant as aJsDate in the active time zone.
     */
    ezCreateFromJsDate(aJsDate) {
        if (!EzObject.isValid(aJsDate)) {
            throw new EzBadParamException(
                'aJsDate',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromJsDate);
        }

        // toISOString() is for the normal JS Date object ONLY
        return EzDateTime.momentWithPTZ(moment(aJsDate.toISOString()));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * @param {moment} momentInstance
     * The moment instance to copy.
     * @returns {moment}
     * A new moment for the same instant as momentInstance in the active time zone.
     */
    static createFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.createFromMoment(momentInstance)
     */
    ezCreateFromMoment = EzDateTime.createFromMoment;

    /**
     * @public
     * @function
     * @description
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the start of the day.
     * @param {moment} momentInstance
     * The moment instance whose date is used.
     * @returns {moment}
     * A new moment in the active time zone for momentInstance's date with the time set to 00:00:00.000.
     */
    ezCreateFromMomentWithTimeStartOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public
     * @function
     * @description
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the end of the day.
     * @param {moment} momentInstance
     * The moment instance whose date is used.
     * @returns {moment}
     * A new moment in the active time zone for momentInstance's date with the time set to 23:59:59.999.
     */
    ezCreateFromMomentWithTimeEndOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeEndOfDay);
        }

        return EzDateTime.withTimeAtEndOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public
     * @function
     * @description
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * The date time string to validate as an ISO 8601 string.
     * @returns {boolean}
     * True if proposedDateTimeIso is a valid ISO 8601 date time string; otherwise, false.
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoDateTime(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public
     * @function
     * @description
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * The date time string to validate as an ISO 8601 string.
     * @returns {boolean}
     * True if proposedDateTimeIso is a valid ISO 8601 date time string; otherwise, false.
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoString(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public
     * @function
     * @description
     * Converts the provided moment to an ISO string
     * @param {moment} momentInstance
     * The moment instance to format as an ISO 8601 string.
     * @returns {string}
     * The ISO 8601 string for momentInstance.
     * @deprecated
     * Migrate to EzDateTime.toIso()
     */
    ezMomentToIsoString(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided moment to one with the preferred timezone
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * TODO: Finish locale work (the optional ezLocale parameter, a valid enum property value from EzLocale, is not accepted yet).
     * @param {moment} momentInstance
     * The moment instance to convert to the active time zone.
     * @returns {moment}
     * A new moment for the same instant as momentInstance in the active time zone (EzDateTime.ezActiveTimeZoneId).
     */
    static momentWithPTZ(momentInstance = null/*, ezLocale */) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.momentWithPTZ);
        }

        /* TODO: Finish locale work
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }
        */

        return moment(momentInstance).tz(EzDateTime.ezActiveTimeZoneId);

        // TODO: Remove below line once verified above line works fine :)
        /* Original implementation:
        return EzDateTime.fromIso(momentInstance.format());
        */
    }

    /**
     * @public
     * @function
     * @description
     * Transforms the provided moment to one with the preferred timezone
     * @param {moment} momentInstance
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentWithPTZ(momentInstance, ezLocale)
     */
    ezMomentWithPTZ = EzDateTime.momentWithPTZ;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a copy of the provided moment that has the Primary Time Zone. If the provided momentInstance is null or not
     * a valid moment instance null is returned.
     * @param {moment} momentInstance
     * The moment instance to copy.
     * @returns {moment}
     * A new moment for the same instant as momentInstance in the active time zone.
     */
    static momentFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.momentFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public
     * @function
     * @description
     * Creates a copy of the provided moment that has the Primary Time Zone. If the provided momentInstance is null or not
     * a valid moment instance null is returned.
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.momentFromMoment(momentInstance)
     */
    ezMomentFromMoment = EzDateTime.momentFromMoment;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the time zone id for the provided momentInstance.
     * If the provided momentInstance is undefined or null, then EzDateTime.ezActiveTimeZoneId is returned.
     * @param {undefined|null|moment} momentInstance
     * The moment instance to get the time zone id from.
     * @returns {string}
     * The time zone id of momentInstance, or EzDateTime.ezActiveTimeZoneId if momentInstance is not a valid moment.
     */
    static getTimeZoneId(momentInstance) {
        return EzDateTime.getTimeZoneIdOrDefault(
            momentInstance,
            EzDateTime.ezActiveTimeZoneId);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the time zone id for the provided momentInstance.
     * If the provided momentInstance is undefined or null, then the provided defaultTimeZoneId value is returned.
     * @param {undefined|null|moment} momentInstance
     * The moment instance to get the time zone id from.
     * @param {string} defaultTimeZoneId
     * The time zone id to return if momentInstance is not a valid moment or has no zone name.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * The time zone id of momentInstance, or defaultTimeZoneId if momentInstance is not valid or has no zone name.
     */
    static getTimeZoneIdOrDefault(momentInstance, defaultTimeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultTimeZoneId;
        }

        return EzString.stringWithLengthOrDefault(
            momentInstance.zone().name,
            defaultTimeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance created from the provided momentInstance then sets the time zone to the
     * provdied timeZoneId.
     * Uses globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone if the provided timeZoneId is not a valid time zone.
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static function: EzDateTime.withZone(momentInstance)
     */
    ezDateTimeWithTimeZone = EzDateTime.withZone;

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Creates a new moment with the provided timeZoneId
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A new moment for the same instant as momentInstance in the safe time zone for timeZoneId.
     */
    static withZone(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withZone);
        }

        return moment(momentInstance).tz(EzDateTime.safeTimeZoneId(timeZoneId));
    }


    /**
     * @public
     * @function
     * @description
     * Creates a new moment with the provided timeZoneId
     * @param {moment} moment
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withZone(moment, timeZoneId)
     */
    ezMomentWithZone = EzDateTime.withZone;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the provided momentInstance in the provided timeZoneId with its time set to the provided hours, minutes, seconds, milliseconds.
     * Important: The time is set after the time-zone is set.
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.#ezActiveTimeZoneId
     * @returns {moment}
     * A new moment for momentInstance in the timeZoneId time zone with the time set to the provided hours, minutes, seconds and milliseconds.
     */
    static withTime(momentInstance = null, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTime);
        }

        return EzDateTime.withZone(
            momentInstance,
            EzDateTime.safeTimeZoneId(timeZoneId))
            .hours(
                EzNumber.numberOrZero(hours))
            .minutes(
                EzNumber.numberOrZero(minutes))
            .seconds(
                EzNumber.numberOrZero(seconds))
            .milliseconds(
                EzNumber.numberOrZero(milliseconds));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A new moment for momentInstance's date in the timeZoneId time zone with the time set to 00:00:00.000.
     */
    static withTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTimeAtStartOfDay);
        }

        return EzDateTime.setTime(
            momentInstance,
            EzNumber.ZERO,
            EzNumber.ZERO,
            EzNumber.ZERO,
            EzNumber.ZERO,
            timeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     * A new moment for momentInstance's date in the timeZoneId time zone with the time set to 23:59:59.999.
     */
    static withTimeAtEndOfDay(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.withTimeAtEndOfDay);
        }

        return EzDateTime.setTime(
            momentInstance,
            EzNumber.TWENTY_THREE,
            EzNumber.FIFTY_NINE,
            EzNumber.FIFTY_NINE,
            EzNumber.NINE_HUNDRED_NINETY_NINE,
            timeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @public
     * @function
     * @description
     * Validates the provided timeZoneId and
     * 1) If valid, returns the provided timeZoneId
     * 2) Returns the EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * The time zone id to validate, e.g. 'America/Chicago'.
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezUserTimeZoneId(timeZoneId);
     */
    ezUseTimezoneIdOrActiveTimezoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.safeTimeZoneId(timeZoneId);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * 3) If EzDateTime.ezActiveTimeZoneId is not a valid date time zone id, then returns moment().tz()
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static safeTimeZoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasText(timeZoneId)) {
            return EzDateTime.timeZoneIdOrDefault(
                EzDateTime.ezActiveTimeZoneId,
                moment().tz());
        }

        return EzDateTime.timeZoneIdOrDefault(
            timeZoneId,
            EzDateTime.ezActiveTimeZoneId);
    }

    /**
     * @public
     * @function
     * @description
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.safeTimeZoneId(timeZoneId)
     */
    ezUseTimeZoneId = EzDateTime.safeTimeZoneId;

    /**
     * @public
     * @function
     * @description
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is a valid date time zone id, then defaultTimeZoneId is returned
     * ====> 2b) If the defaultTimeZoneId is not a valid date time zone id, then moment().tz() is returned.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static timeZoneIdOrDefault(timeZoneId = null, defaultTimeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (EzDateTime.DEFAULT_ACTIVE_TIMEZONE === timeZoneId.toUpperCase()) {
            timeZoneId = EzDateTime.ezGuessLocalTimeZone();
        }

        if (!EzString.hasText(defaultTimeZoneId) || !EzDateTime.isValidTimeZone(defaultTimeZoneId)) {
            defaultTimeZoneId = moment().tz();
        }

        return EzDateTime.isValidTimeZone(timeZoneId)
            ? timeZoneId
            : defaultTimeZoneId;
    }

    /**
     * @public
     * @function
     * @description
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is provided to the method, then return the provided defaultTimeZoneId
     * ====>2b) The defaultTimeZoneId is NOT provided to the method, then return the provided defaultTimeZoneId
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.timeZoneIdOrDefault(timeZoneId, defaultTimeZoneId)'
     */
    ezUseTimeZoneIdOrDefault = EzDateTime.timeZoneIdOrDefault;

    /**
     * @static
     * @public
     * @description
     * Gets the default active time zone display HTML element id
     * @returns {string}
     * The default HTML element id ('_EzActiveTimeZone') used to display the active time zone.
     */
    static get defaultActiveTimeZoneDisplayElementId() {
        return '_EzActiveTimeZone';
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} displayActiveTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: EzDateTime.defaultActiveTimeZoneDisplayElementId (aka '_EzActiveTimeZone')
     */
    static displayActiveTimezone(displayActiveTimeZoneElementId = EzDateTime.defaultActiveTimeZoneDisplayElementId) {
        const htmlElement = globalThis?.document?.getElementById(
            EzString.stringWithTextOrDefault(
                displayActiveTimeZoneElementId,
                EzDateTime.defaultActiveTimeZoneDisplayElementId));

        if (EzObject.isValid(htmlElement)) {
            htmlElement.innerHtml = `Active Timezone: ${EzDateTime.ezActiveTimeZoneId}`;
        }
    }

    /**
     * @public
     * @function
     * @description
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} displayActiveTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: '_EzActiveTimeZone'
     * @deprecated
     * Migrate to static method: EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
     */
    ezDisplayActiveTimezone(displayActiveTimeZoneElementId) {
        EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The whole number of years from endMomentInstance to startMomentInstance (startMomentInstance.diff(endMomentInstance, 'years')); negative if start is before end.
     */
    static yearsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'years');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.yearsBetween(startMomentInstance, endMomentInstance)
     */
    ezYearsBetween = EzDateTime.yearsBetween;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The whole number of months from endMomentInstance to startMomentInstance (startMomentInstance.diff(endMomentInstance, 'months')); negative if start is before end.
     */
    static monthsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'months');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     *@param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    ezMonthsBetween = EzDateTime.monthsBetween;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The whole number of weeks from endMomentInstance to startMomentInstance (startMomentInstance.diff(endMomentInstance, 'weeks')); negative if start is before end.
     */
    static weeksBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'weeks');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.weeksBetween(startMomentInstance, endMomentInstance)
     */
    ezWeeksBetween = EzDateTime.weeksBetween;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The whole number of days from endMomentInstance to startMomentInstance (startMomentInstance.diff(endMomentInstance, 'days')); negative if start is before end.
     */
    static daysBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'days');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.daysBetween(startMomentInstance, endMomentInstance)
     */
    ezDaysBetween = EzDateTime.daysBetween;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The whole number of hours from endMomentInstance to startMomentInstance (startMomentInstance.diff(endMomentInstance, 'hours')); negative if start is before end.
     */
    static hoursBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'hours');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.hoursBetween(startMomentInstance, endMomentInstance)
     */
    ezHoursBetween = EzDateTime.hoursBetween;

    /**
     * @static
     * @public
     * @function
     * @description
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The number of minutes between startMomentInstance and endMomentInstance (startMomentInstance.diff(endMomentInstance, 'minutes')).
     */
    static minutesBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'minutes');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.minutesBetween(startMomentInstance, endMomentInstance)
     */
    ezMinutesBetween = EzDateTime.minutesBetween;

    /**
     * @public
     * @function
     * @description
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * The number of seconds between startMomentInstance and endMomentInstance (startMomentInstance.diff(endMomentInstance, 'seconds')).
     */
    static secondsBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'seconds');
    }

    /**
     * @public
     * @function
     * @description
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.secondsBetween(startMomentInstance, endMomentInstance)
     */
    ezSecondsBetween = EzDateTime.secondsBetween;

    /**
     * @public
     * @function
     * @description
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefined|null|string} timeZoneId
     * The time zone id used to determine the current date and time, e.g. 'America/Chicago'.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixMSTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).format(EzChar.X);
    }

    /**
     * @public
     * @function
     * @description
     * Generates a timestamp for the current date + time in the UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixMSTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format(EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefined|null|string} timeZoneId
     * The time zone id used to determine the current date and time, e.g. 'America/Chicago'.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzString.hasLength(timeZoneId)
            ? EzDateTime.now().format(EzChar.x)
            : EzDateTime.now().withZone(timeZoneId).format(EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * Generates a timestamp for the current date + time in UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format(EzChar.x);
    }

    /**
     * @public
     * @function
     * @description
     * Generates a timestamp for the provided momentInstance or NOW if momentInstance is not provided/null.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefined|null|moment} momentInstance
     * The moment instance to generate the timestamp for; throws EzBadParamException if not valid.
     * @param {undefined|null|string} timeZoneId
     * The time zone id to convert the moment instance to before generating the timestamp, e.g. 'America/Chicago'.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetUnixTimeStamp(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetUnixTimeStamp);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .withZone(
                EzDateTime.timeZoneIdOrDefault(timeZoneId))
            .format(EzChar.x);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets a time picker's values from the provided moment
     * @param {string} timePickerId
     * The HTML element id (or jQuery selector) of the jQuery time picker input to set.
     * @param {moment} [momentInstance]
     * Default: null
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     */
    static setTimePickerValue(timePickerId, momentInstance = null) {
        if (!EzString.hasText(timePickerId)) {
            throw new EzBadParamException(
                { timePickerId },
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }

        const timePickerElement = EzUx.$id(timePickerId);

        if (!EzObject.isValid(timePickerElement)) {
            throw new EzBadStateException(
                EzString.em`
                    Expected the provided timePickerId of "${timePickerId}" to be a valid HTML element id or jQuery selector
                    that is associated with a valid HTML element.`,
                EzString.em`
                    However, the provided timePickerId of ${timePickerId} is either
                    a valid HTML element or jQuery selector but not associated with a valid HTML elementnot
                    or is not valid HTML element id or jQuery selector.`,
                EzDateTime,
                EzDateTime.setTimePickerValue);
        }

        timePickerElement.timepicker(
            'setTime',
            EzDateTime.momentWithPTZ(momentInstance)
                .format(EzDateTime.DTFORMAT_hh_mm_a));
    }

    /**
     * @public
     * @function
     * @description
     * Sets a time picker's values from the provided moment
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * The provided momentInstance is converted to the active time zone before setting the time picker's values.
     * @deprecated
     * Migreate to static method: EzDateTime.setTimePickerValue(timePickerId, momentInstance)
     */
    ezSetTimePickerValue = EzDateTime.setTimePickerValue;

    /**
     * @public
     * @function
     * @description
     * Calculates the elapsed time since the provided startMoment to right now
     * @param {moment} startMoment
     * The moment instance the elapsed time is measured from.
     * @returns {string}
     * The elapsed time from startMoment to now as an HH:MM string, or '00:00' if startMoment is not a valid moment.
     */
    ezCalculateElapsedTimeToNow(startMoment) {
        return EzDateTime.isValidMoment(startMoment)
            ? EzDateTime.ezInstance.ezDurationToShiftTotal(
                moment.duration(
                    EzDateTime.now().diff(startMoment)))
            : '00:00';
    }

    /**
     * @public
     * @function
     * @description
     * Returns a duration as HH:MM string.
     * @param {moment.duration} duration
     * The moment.js duration to format as hours and minutes.
     * @returns {string}
     * The duration formatted as a zero-padded HH:MM string, or '00:00' if duration is not valid.
     */
    ezDurationToShiftTotal(duration) {
        if (!EzObject.isValid(duration)) {
            return '00:00';
        }

        const hours = duration.hours();

        const min = duration.minutes();

        let response = EzNumber.TEN > hours
            ? `0${hours.toString()}`
            : hours.toString();

        response += EzNumber.TEN > min
            ? `:0${min.toString()}`
            : `:${min.toString()}`;

        return response;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the day's name for the provided momentInstance
     * @param {moment} momentInstance
     * The moment instance to get the day name for (converted to the active time zone first).
     * @returns {string}
     * The full name of the day of the week for the moment, e.g. 'Monday' (moment.js 'dddd' format).
     */
    ezGetMomentDayName(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMomentDayName);
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('dddd');
    }

    /**
     * Builds a shift total string from the provided values.
     * Null or zero values not displayed. Returns formats:
     *     D day HH:MM:SS.sss
     *     DD days HH:MM:SS.sss
     *     HH:MM:SS.sss
     *     HH:MM:SS
     *     HH:MM
     *     HH
     *     00:MM
     *     00:MM:SS
     *     00:MM:SS.sss
     *     00:00:SS
     *     00:00:SS.sss
     *     00:00:00.SSS
     * @param {boolean} includeDays
     * True to include the days value in the result.
     * @param {number|null} days
     * The number of days to display (only used if includeDays is true).
     * @param {boolean} includeHours
     * True to include the hours value (and optionally minutes, seconds, and milliseconds) in the result.
     * @param {number|null} hours
     * The number of hours to display (only used if includeHours is true).
     * @param {boolean} includeMinutes
     * True to include the minutes value in the result (only applies if includeHours is true).
     * @param {number|null} minutes
     * The number of minutes to display (only used if includeMinutes is true).
     * @param {boolean} includeSeconds
     * True to include the seconds value in the result (only applies if includeMinutes is true).
     * @param {number|null} seconds
     * The number of seconds to display (only used if includeSeconds is true).
     * @param {boolean} includeMilliseconds
     * True to include the milliseconds value in the result (only applies if includeSeconds is true).
     * @param {number|null} milliseconds
     * The number of milliseconds to display (only used if includeMilliseconds is true).
     * @returns {string}
     * The shift total display string built from the included values, e.g. '2 days 08:30:15.250'.
     */
    ezBuildShiftTotalString(includeDays, days, includeHours, hours, includeMinutes, minutes, includeSeconds, seconds, includeMilliseconds, milliseconds) {
        let result = EzBoolean.isTrue(includeDays) && EzNumber.isNumber(days)
            ? EzDateTime.ezInstance.ezBuildShiftTotalDays(days, true)
            : EzString.EMPTY;

        if (EzBoolean.isTrue(includeHours)) {
            result = `${result}${EzDateTime.ezInstance.ezBuildShiftTotalHours(hours)}`;

            // Minutes (only if hours included)
            if (EzBoolean.isTrue(includeMinutes)) {
                result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalMinutes(minutes, true)}`;

                // Seconds (only if minutes included)
                if (EzBoolean.isTrue(includeSeconds)) {
                    result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalSeconds(seconds, true)}`;

                    // Milliseconds (only if seconds included)
                    if (EzBoolean.isTrue(includeMilliseconds)) {
                        result = `${result}.${EzDateTime.ezInstance.ezBuildShiftTotalMilliseconds(milliseconds, true)}`;
                    }
                }
            }
        }

        return result;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a shift total display string for days using the provided days.
     * Appends a space if the appendSpace boolean is true.
     * @param {number} days
     * The number of days to display; zero is used if not a number.
     * @param {boolean} appendSpace
     * True to place a space between the days value and the day/days suffix.
     * Default: false
     * @returns {string}
     * The days display string, e.g. '1 day' or '3 days' ('3days' when appendSpace is false).
     */
    ezBuildShiftTotalDays(days, appendSpace = false) {
        days = EzNumber.isNumber(days)
            ? days
            : EzNumber.ZERO;

        const daySuffix = 1 === days
            ? 'day'
            : 'days';

        return EzBoolean.isTrue(appendSpace)
            ? `${days} ${daySuffix}`
            : `${days}${daySuffix}`;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a shift total display string for hours using the provided hours.
     * Prepends a space if prependSpace boolean is true.
     * @param {number|null} hours
     * The number of hours to display; zero is used if not a number.
     * @param {boolean} prependSpace
     * True to prepend a space to the resulting hours string.
     * Default: false
     * @returns {string}
     * The hours display string (ISO formatted hours), optionally prefixed with a space.
     */
    ezBuildShiftTotalHours(hours, prependSpace = false) {
        hours = EzNumber.isNumber(hours)
            ? EzDateTime.ezInstance.ezHoursToIso(hours)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependSpace)
            ? ` ${hours}`
            : `${hours}`;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a shift total in minutes. Prepends the : if prependColon boolean is true.
     * @param {number} minutes
     * The number of minutes to display; zero is used if not a number.
     * @param {boolean|null|undefined} prependColon
     * True to prepend a colon to the resulting minutes string.
     * Default: false
     * @returns {string}
     * The minutes display string (ISO formatted minutes), optionally prefixed with a colon.
     */
    ezBuildShiftTotalMinutes(minutes, prependColon = false) {
        minutes = EzNumber.isNumber(minutes)
            ? EzDateTime.ezInstance.ezMinutesToIso(minutes)
            : EzNumber.ZERO;

        return prependColon
            ? `:${minutes}`
            : `${minutes}`;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a shift total display string for seconds using the provided seconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} seconds
     * The number of seconds to display; zero is used if not a number.
     * @param {boolean|null|undefined} prependColon
     * True to prepend a colon to the resulting seconds string.
     * @returns {string}
     * The seconds display string (ISO formatted seconds), optionally prefixed with a colon.
     */
    ezBuildShiftTotalSeconds(seconds, prependColon) {
        seconds = EzNumber.isNumber(seconds)
            ? EzDateTime.ezInstance.ezSecondsToIso(seconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${seconds}`
            : `${seconds}`;
    }

    /**
     * @public
     * @function
     * @description
     * Builds a shift total display string for milliseconds using the provided milliseconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} milliseconds
     * The number of milliseconds to display; zero is used if not a number.
     * @param {boolean|null|undefined} prependColon
     * True to prepend a colon to the resulting milliseconds string.
     * Default: false
     * @returns {string}
     * The milliseconds display string (ISO formatted milliseconds), optionally prefixed with a colon.
     */
    ezBuildShiftTotalMilliseconds(milliseconds, prependColon = false) {
        milliseconds = EzNumber.isNumber(milliseconds)
            ? EzDateTime.ezInstance.ezMillisecondsToIso(milliseconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${milliseconds}`
            : `${milliseconds}`;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided timeHours number as an ISO minutes string.
     * @param {number} hours
     * The hours number to convert; throws EzBadParamException if not a number.
     * @returns {string}
     * The hours as a zero-padded ISO hours string (e.g. '08'), or '00' if out of range.
     */
    ezHoursToIso(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezHoursToIso);
        }

        const hoursIso = EzNumber.ZERO >= hours && 10 <= hours
            ? hours.toString()
            : `0${hours.toString()}`;

        return EzNumber.ZERO >= hours
            ? hoursIso
            : '00';
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided timeMinutes number as an ISO minutes string of two digits.
     * @param {number} minutes
     * The minutes number to convert; throws EzBadParamException if not a number.
     * @returns {string}
     * The minutes as a two-digit ISO minutes string (e.g. '05'), or '00' if out of range.
     */
    ezMinutesToIso(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinutesToIso);
        }

        const minutesIso = EzNumber.ZERO >= minutes && 10 <= minutes
            ? minutes.toString()
            : `0${minutes.toString()}`;

        return EzNumber.ZERO >= minutes && 59 <= minutes
            ? minutesIso
            : '00';
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided seconds number as a ISO seconds string of two digits.
     * @param {number} seconds
     * The seconds number to convert; throws EzBadParamException if not a number.
     * @returns {string}
     * The seconds as a two-digit ISO seconds string (e.g. '09'), or '00' if out of range.
     */
    ezSecondsToIso(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSecondsToIso);
        }

        const secondsIso = EzNumber.ZERO >= seconds && 10 <= seconds
            ? seconds.toString()
            : `0${seconds.toString()}`;

        return EzNumber.ZERO >= seconds && 59 <= seconds
            ? secondsIso
            : '00';
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided timeMilliseconds number to an ISO milliseconds string.
     * @param {number} milliseconds
     * The milliseconds number to convert; throws EzBadParamException if not a number.
     * @returns {string}
     * The milliseconds as a three-digit ISO milliseconds string (e.g. '050'), or '000' if out of range.
     */
    ezMillisecondsToIso(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'timeMilliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToIso);
        }

        if (0 < milliseconds && 999 <= milliseconds) {
            if (10 > milliseconds) {
                return `00${milliseconds}`;
            }

            if (100 > milliseconds) {
                return `0${milliseconds}`;
            }

            return `${milliseconds}`;
        }

        return '000';
    }

    /**
     * @public
     * @function
     * @description
     * Calculates the duration between two moments.
     * @param {moment} startMoment
     * The moment instance at the start of the duration.
     * @param {moment} endMoment
     * The moment instance at the end of the duration.
     * @returns {moment.duration}
     * The moment.js duration from startMoment to endMoment, or a zero duration if either moment is not valid.
     */
    ezCalculateDuration(startMoment, endMoment) {
        if (!EzObject.isValid(startMoment) || !EzObject.isValid(endMoment)) {
            return moment.duration(EzNumber.ZERO);
        }

        startMoment = EzDateTime.momentWithPTZ(startMoment);

        endMoment = EzDateTime.momentWithPTZ(endMoment);

        return moment.duration(endMoment.diff(startMoment));
    }

    /**
     * @public
     * @function
     * @description
     * Returns the milliseconds of the provided moment.
     * @param {moment} momentInstance
     * The moment instance to get the milliseconds from; throws EzBadParamException if not valid.
     * @returns {number}
     * The milliseconds (0 to 999) of the moment converted to the active time zone.
     */
    ezGetMillisecond(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMillisecond);
        }

        return EzDateTime.momentWithPTZ(momentInstance).milliseconds();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the seconds of the provided moment.
     * @param {moment} momentInstance
     * The moment instance to get the seconds from; throws EzBadParamException if not valid.
     * @returns {number}
     * The seconds (0 to 59) of the moment converted to the active time zone.
     */
    ezGetSeconds(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetSeconds);
        }

        return EzDateTime.momentWithPTZ(momentInstance).seconds();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the minutes of the provided moment.
     * @param {moment} momentInstance
     * The moment instance to get the minutes from; throws EzBadParamException if not valid.
     * @returns {number}
     * The minutes (0 to 59) of the moment converted to the active time zone.
     */
    ezGetMinutes(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMinutes);
        }

        return EzDateTime.momentWithPTZ(momentInstance).minutes();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the hours of the provided moment.
     * @param {moment} momentInstance
     * The moment instance to get the hours from; throws EzBadParamException if not valid.
     * @returns {number}
     * The hours (0 to 23) of the moment converted to the active time zone.
     */
    ezGetHours(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetHours);
        }

        return EzDateTime.momentWithPTZ(momentInstance).hours();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the month of the provided moment
     * Returned value is 0 through 11 where 0 = Janurary and 11 = December.
     * @param {moment} momentInstance
     * The moment instance to get the month from; throws EzBadParamException if not valid.
     * @returns {number}
     * The zero-based month (0 = January through 11 = December) of the moment converted to the active time zone.
     */
    ezGetMonth(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance).month();
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Returns the day of the provided moment (a value from 1 to 31)
     * @param {moment} momentInstance
     * The moment instance to get the day of the month from; throws EzBadParamException if not valid.
     * @returns {number}
     * The day of the month (1 to 31) of the moment converted to the active time zone.
     */
    static getDay(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.getDay);
        }

        return EzDateTime.momentWithPTZ(momentInstance).date();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the day of the provided moment (a value from 1 to 31)
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.getDay(momentInstance)
     */
    ezGetDay = EzDateTime.getDay;

    /**
     * @public
     * @function
     * @description
     * Returns the year of the provided moment
     * @param {moment} momentInstance
     * The moment instance to get the year from; throws EzBadParamException if not valid.
     * @returns {number}
     * The year of the moment converted to the active time zone, e.g. 2026.
     */
    ezGetYear(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetYear);
        }

        return EzDateTime.momentWithPTZ(momentInstance).year();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided hours as a decimal hours value.
     * @param {number} hours
     * The number of hours to convert; throws EzBadParamException if not a number.
     * @returns {number}
     * The provided hours as decimal hours (returned unchanged).
     */
    ezHoursToDecimalHours(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return hours;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided minutes as a decimal hours value.
     * @param {number} minutes
     * The number of minutes to convert; throws EzBadParamException if not a number.
     * @returns {number}
     * The provided minutes converted to decimal hours (minutes / 60).
     */
    ezMinutesToDecimalHours(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return minutes / 60;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided milliseconds as a decimal hours value.
     * @param {number} seconds
     * The number of seconds to convert; throws EzBadParamException if not a number.
     * @returns {number}
     * The provided seconds converted to decimal hours (seconds / 3600).
     */
    ezSecondsToDecimalHours(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return seconds / 3600;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the provided milliseconds as a decimal hour value.
     * @param {number} milliseconds
     * The number of milliseconds to convert; throws EzBadParamException if not a number.
     * @returns {number}
     * The provided milliseconds converted to decimal hours (milliseconds / 3600000).
     */
    ezMillisecondsToDecimalHours(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance
                , EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return milliseconds / 3600000;
    }

    /**
     * @public
     * @function
     * @description
     * Returns the duration between the provided moment instances as decimal hours.
     * Provide the number of preferred deci+-malPoints. The default is 2.
     * @param {moment} momentInstance1
     * The later (end) moment instance; the duration is calculated as momentInstance1.diff(momentInstance2).
     * @param {moment} momentInstance2
     * The earlier (start) moment instance subtracted from momentInstance1.
     * @param {number} decimalPoints
     * The number of decimal places in the result; 2 is used if not a number.
     * @returns {string}
     * The duration between the two moments in decimal hours fixed to decimalPoints places, or zero if either moment is not valid.
     */
    ezDurationBetweenAsDecimalHours(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = EzNumber.TWO;
        }

        let decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? EzNumber.ZERO
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.ezInstance.ezMomentWithPTZ(momentInstance1)
                        .diff(EzDateTime.ezInstance.ezMomentWithPTZ(momentInstance2))));

        decimalHours = Math.round(decimalHours * (10 ^ decimalPoints)) / (10 ^ decimalPoints);

        return Number.parseFloat(decimalHours).toFixed(decimalPoints);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the duration between the provided moment instances as decimal days.
     * @param {moment} momentInstance1
     * The later (end) moment instance; the duration is calculated as momentInstance1.diff(momentInstance2).
     * @param {moment} momentInstance2
     * The earlier (start) moment instance subtracted from momentInstance1.
     * @param {number} decimalPoints
     * The number of decimal places in the result; 2 is used if not a number.
     * @returns {string}
     * The duration between the two moments in decimal days fixed to decimalPoints places, or zero if either moment is not valid.
     */
    ezDurationBetweenAsDecimalDays(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = EzNumber.TWO;
        }

        const decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? EzNumber.ZERO
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.momentWithPTZ(momentInstance1)
                        .diff(EzDateTime.momentWithPTZ(momentInstance2))));

        let decimalDays = decimalHours / EzNumber._24;

        decimalDays = Math.round(decimalDays * (EzNumber.TEN ^ decimalPoints)) / (EzNumber.TEN ^ decimalPoints);

        return Number.parseFloat(decimalDays).toFixed(decimalPoints);
    }

    /**
     * @public
     * @function
     * @description
     * Returns the momentjs duration instance for the provided momentjs moment instance.
     * @param {moment} momentInstance
     * The moment instance to get the duration for; throws EzBadParamException if not valid.
     * @returns {object}
     * The moment.js duration instance returned by momentInstance.duration().
     */
    ezDurationForMoment(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                { momentInstance },
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDurationForMoment);
        }

        return momentInstance.duration();
    }

    /**
     * @public
     * @function
     * @description
     * Returns the duration between the provided moment instances formatted as hours and minutes (for example: '08h 05m').
     * @param {moment} momentInstance1
     * The later (end) moment instance; the duration is calculated as momentInstance1.diff(momentInstance2).
     * @param {moment} momentInstance2
     * The earlier (start) moment instance subtracted from momentInstance1.
     * @returns {string}
     * The duration formatted as zero-padded hours and minutes, e.g. '08h 05m'.
     */
    ezDurationBetweenAsHoursMinutes(momentInstance1, momentInstance2) {
        const millisecondsDuration = EzDateTime.momentWithPTZ(momentInstance1)
            .diff(EzDateTime.momentWithPTZ(momentInstance2));

        const hours = Math.trunc(millisecondsDuration / EzNumber.MILLISECONDS_IN_HOUR);

        const minutes = Math.round((millisecondsDuration - hours * EzNumber.MILLISECONDS_IN_HOUR) / EzNumber.MILLISECONDS_IN_MINUTE);

        const hoursStr = EzNumber.TEN > hours
            ? `0${hours}`
            : `${hours}`;

        return EzNumber.TEN > minutes
            ? `${hoursStr}h 0${minutes}m`
            : `${hoursStr}h ${minutes}m`;
    }

    static isFirstHalfOfMonth(momentInstance = null) {
        return EzDateTime.getDay(momentInstance) <= 15;
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceA is the same as the provided momentInstanceB
     * Alias for: EzDateTime.isSame(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|unknown} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     * True if both moments have the same ISO 8601 value or both are null/undefined; otherwise, false.
     */
    static equals(momentInstanceA = null, momentInstanceB = null) {
        if (!EzObject.isValid(momentInstanceA)) {
            return !EzObject.isValid(momentInstanceB);
        }

        if (!EzObject.isValid(momentInstanceB)) {
            return false;
        }

        return EzDateTime.toIso(momentInstanceA) === EzDateTime.toIso(momentInstanceB);
    }

    /**
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceA is the same as the provided momentInstanceB
     * Alias for: EzDateTime.isSame(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|unknown} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.equals(momentInstanceA, momentInstanceB)
     */
    ezEquals = EzDateTime.equals;

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentA is the same as the provided momentB
     * Alias for: EzDateTime.equals(momentInstanceA, momentInstanceB)
     * @param {moment} momentInstanceA
     * Will throw EzBadParamException if momentInstanceA is not a valid non-null moment instance
     * unless momentInstanceB is also null and in such a case true (meaning both are equally null) is returned.
     * Default: null
     * @param {undefined|null|moment|unknown} momentInstanceB
     * Can be undefined, null, a valid moment instance, or anything else.
     * Default: null
     * @returns {boolean}
     * True if both moments have the same ISO 8601 value or both are null/undefined; otherwise, false.
     */
    static isSame(momentInstanceA = null, momentInstanceB = null) {
        return EzDateTime.equals(momentInstanceA, momentInstanceB);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceToCheck is the same as or before the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * The moment instance to check; throws EzBadParamException if not valid.
     * @param {moment} compareToMomentInstance
     * The moment instance to compare against; throws EzBadParamException if not valid.
     * @returns {boolean}
     * True if momentInstanceToCompare is the same as or before compareToMomentInstance; otherwise, false.
     */
    static isEqualOrBefore(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return EzDateTime.equals(momentInstanceToCompare, compareToMomentInstance) ||
            momentInstanceToCompare.isBefore(compareToMomentInstance);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceToCheck is before the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * The moment instance to check; throws EzBadParamException if not valid.
     * @param {moment} compareToMomentInstance
     * The moment instance to compare against; throws EzBadParamException if not valid.
     * @returns {boolean}
     * True if momentInstanceToCompare is before compareToMomentInstance; otherwise, false.
     */
    static isBefore(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return momentInstanceToCompare.isBefore(compareToMomentInstance);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceToCheck is the same as or after the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid non-null moment instance.
     * @param {moment} momentInstanceToCompare
     * The moment instance to check; throws EzBadParamException if not valid.
     * @param {moment} compareToMomentInstance
     * The moment instance to compare against; throws EzBadParamException if not valid.
     * @returns {boolean}
     * True if momentInstanceToCompare is the same as or after compareToMomentInstance; otherwise, false.
     */
    static isEqualOrAfter(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                'momentInstanceToCompare',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                'compareToMomentInstance',
                EzDateTime,
                EzDateTime.isEqualOrBefore);
        }

        return EzDateTime.equals(momentInstanceToCompare, compareToMomentInstance) ||
            momentInstanceToCompare.isAfter(compareToMomentInstance);
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the provided momentInstanceToCheck is after the provided momentInstance
     * Will throw EzBadParamException if either momentInstanceToCheck or momentInstance is not a valid moment instance.
     * @param {moment} momentInstanceToCompare
     * The moment instance to check; throws EzBadParamException if not valid.
     * @param {moment} compareToMomentInstance
     * The moment instance to compare against; throws EzBadParamException if not valid.
     * @returns {boolean}
     * True if momentInstanceToCompare is after compareToMomentInstance; otherwise, false.
     */
    static isAfter(momentInstanceToCompare, compareToMomentInstance) {
        if (!EzObject.isValid(momentInstanceToCompare)) {
            throw new EzBadParamException(
                { momentInstanceToCompare },
                EzDateTime,
                EzDateTime.isAfter);
        }
        if (!EzObject.isValid(compareToMomentInstance)) {
            throw new EzBadParamException(
                { compareToMomentInstance },
                EzDateTime,
                EzDateTime.isAfter);
        }

        return momentInstanceToCompare.isAfter(compareToMomentInstance);
    }

    /**
     * @protected
     * @function
     * @description
     * Handles the onSelectedEmployerReady event from EzClockerContext
     */
    ezHandleOnSelectedEmployerReady() {
        if (EzBoolean.isFalse(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzDateTime.ezLoadPreferredTimezoneOption();
        }
    }

    /**
     * @protected
     * @function
     * @description
     * Handles the OnUserContextReady event from EzClockerContext
     */
    ezHandleOnUserContextReady() {
        EzDateTime.ezInstance.ezLoadAuthenticatedUserPreferredLocaleOption();
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Determines if the value of the HTML element with the provided id is a valid moment string that can be parsed to a moment instance.
     * Will return false if the element is not found or if the element's value is not a valid moment string.
     * @param {string} [elementId]
     * Default: EzString.EMPTY
     * @returns {boolean}
     * True if the element exists and its value parses to a valid moment; otherwise, false.
     */
    static isValidMomentFromElementId(elementId = EzString.EMPTY) {
        const element = EzUx.$id(elementId);

        return EzObject.isValid(element) && moment(element.val()).isValid();
    }

    /**
     * @public
     * @function
     * @param {string} elementId
     * @returns {boolean}
     */
    ezIsValidMomentFromElementId = EzDateTime.isValidMomentFromElementId;

    /**
     * @public
     * @function
     * @description
     * Returns a moment for the first day of week based upon what is indicated as the firstDayOfWeek
     * @param {moment} momentInWeek
     * A moment instance within the week to find the first day of; throws EzBadParamException if not valid.
     * @param {number} firstDayOfWeek
     * The day of the week the week starts on (0 = Sunday through 6 = Saturday).
     * Default: EzNumber.ZERO
     * @returns {moment}
     * A new moment set to the start of day on the first day of the week containing momentInWeek.
     */
    ezCalculateFirstDayOfWeekFromMoment(momentInWeek, firstDayOfWeek = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(momentInWeek)) {
            throw new EzBadParamException(
                'momentInWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }
        if (!EzNumber.isNumber(firstDayOfWeek)) {
            throw new EzBadParamException(
                'firstDayOfWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }

        const dayOfPeriodStart = momentInWeek.day();

        if (EzNumber.ZERO < dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
                .subtract(
                    dayOfPeriodStart - firstDayOfWeek,
                    'days');
        }

        if (EzNumber.ZERO === dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek);
        }

        return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
            .subtract(
                EzNumber.SEVEN + (dayOfPeriodStart - firstDayOfWeek),
                'days');
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the hours of the provided aMoment value.
     * @param {moment} aMoment
     * The moment instance to set the hours on; throws EzBadParamException if not valid.
     * @param {number} [hours]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 23.
     * @returns {moment}
     * A copy of aMoment (in the active time zone) with the hours set to the provided value.
     */
    static setHours(aMoment, hours = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours);
        }
        if (EzNumber.ZERO > hours || EzNumber._23 < hours) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours,
                'The hours param must be a positive number from 0 to 23.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .hours(
                EzNumber.positiveNumberOrZero(hours));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the minutes of the provided aMoment value.
     * @param {moment} aMoment
     * The moment instance to set the minutes on; throws EzBadParamException if not valid.
     * @param {number} [minutes]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 59.
     * @returns {moment}
     * A copy of aMoment (in the active time zone) with the minutes set to the provided value.
     */
    static setMinutes(aMoment, minutes = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes);
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes,
                'The minutes param must be a positive number from 0 to 59.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .minutes(
                EzNumber.positiveNumberOrZero(minutes));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the seconds of the provided aMoment value.
     * @param {moment} aMoment
     * The moment instance to set the seconds on; throws EzBadParamException if not valid.
     * @param {number} [seconds]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 59.
     * @returns {moment}
     * A copy of aMoment (in the active time zone) with the seconds set to the provided value.
     */
    static setSeconds(aMoment, seconds = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes);
        }
        if (EzNumber.ZERO > seconds || EzNumber._59 < seconds) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setSeconds,
                'The seconds param must be a positive number from 0 to 59.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .seconds(
                EzNumber.positiveNumberOrZero(seconds));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the milliseconds of the provided aMoment value.
     * @param {moment} aMoment
     * The moment instance to set the milliseconds on; throws EzBadParamException if not valid.
     * @param {number} [milliseconds]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 999.
     * @returns {moment}
     * A copy of aMoment (in the active time zone) with the milliseconds set to the provided value.
     */
    static setMilliseconds(aMoment, milliseconds = EzNumber.ZERO) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds);
        }
        if (EzNumber.ZERO > milliseconds || EzNumber._999 < milliseconds) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds,
                'The milliseconds param must be a positive number from 0 to 999.');
        }

        return EzDateTime.momentWithPTZ(aMoment)
            .milliseconds(
                EzNumber.positiveNumberOrZero(milliseconds));
    }

    /**
     * @static
     * @public
     * @function
     * @description
     * Sets the time of the provided aMoment value using the provided hours, minutes, seconds and milliseconds values.
     * Will throw EzBadParamException if aMoment is not a valid moment instance or if any of the provided hours, minutes, seconds or milliseconds values are
     * out of their acceptable ranges.
     * @param {moment} aMoment
     * The moment instance to set the time on; throws EzBadParamException if not valid.
     * @param {number} [hours]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 23.
     * @param {number} [minutes]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 59.
     * @param {number} [seconds]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 59.
     * @param {number} [milliseconds]
     * Default: EzNumber.ZERO
     * Positive number from 0 to 999.
     * @param {string} [timeZoneId]
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * A copy of aMoment in the timeZoneId time zone with its hours, minutes, seconds, and milliseconds set to the provided values.
     */
    static setTime(aMoment, hours = EzNumber.ZERO, minutes = EzNumber.ZERO, seconds = EzNumber.ZERO, milliseconds = EzNumber.ZERO,
        timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw new EzBadParamException(
                'aMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds);
        }
        if (EzNumber.ZERO > hours || EzNumber._23 < hours) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setHours,
                'The hours param must be a positive number from 0 to 23.');
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMinutes,
                'The minutes param must be a positive number from 0 to 59.');
        }
        if (EzNumber.ZERO > minutes || EzNumber._59 < minutes) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setSeconds,
                'The seconds param must be a positive number from 0 to 59.');
        }
        if (EzNumber.ZERO > milliseconds || EzNumber._999 < milliseconds) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.setMilliseconds,
                'The milliseconds param must be a positive number from 0 to 999.');
        }

        return EzDateTime.withZone(aMoment, timeZoneId)
            .hours(
                EzNumber.positiveNumberOrZero(hours))
            .minutes(
                EzNumber.positiveNumberOrZero(minutes))
            .seconds(
                EzNumber.positiveNumberOrZero(seconds))
            .milliseconds(
                EzNumber.positiveNumberOrZero(milliseconds));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! IMPORTANT: Enter ONLY class initialization or EzApi registration code in this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     * The ezApi registration name of the EzDateTime singleton instance ('ezDateTime').
     */
    static get ezApiName() {
        return 'ezDateTime';
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Returns an object of event names triggered by EzDateTime
     * @returns {object}
     * An object mapping event keys (onReady, onActiveTimeZoneLoaded, etc.) to the event names EzDateTime triggers.
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzDateTime_Ready',
            onActiveTimeZoneLoaded: 'ezOn_EzDateTime_ActiveTimeZone_Loaded',
            onActiveTimeZoneChanged: 'ezOn_EzDateTime_ActiveTimeZone_Changed',
            onSupportedTimeZonesReady: 'ezOn_EzDateTime_SupportedTimeZones_Ready'
        };
    }

    /**
     * @static
     * @private
     * @description
     * Stores the reference to EzDateTime's singleton instance registered with globalThis.ezApi.
     * @type {EzDateTime}
     */
    static #ezInstance = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? globalThis.ezApi.ezclocker[EzDateTime.ezApiName]
        : null;
    /**
     * @static
     * @public
     * @description
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @returns {EzDateTime}
     * The EzDateTime singleton instance registered with ezApi, or null if not yet registered.
     */
    static get ezInstance() {
        return EzDateTime.#ezInstance;
    }
    /**
     * @static
     * @public
     * @description
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @param {EzDateTime} instance
     * The EzDateTime singleton instance to store; throws an Error if an instance is already registered.
     */
    static set ezInstance(instance) {
        if (null != EzDateTime.#ezInstance) {
            throw new Error('EzDateTime\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzDateTime.#ezInstance = instance;

        if (null != EzDateTime.#ezInstance) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.REGISTERED;
        }
    }

    /**
     * @static
     * @private
     * @type {string}
     */
    static #ezApiRegistrationState = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public
     * @description
     * Returns the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzDateTime.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public
     * @description
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzDateTime.#ezApiRegistrationState = ezApiRegistrationState;
    }

    /**
     * @static
     * @public
     * @readonly
     * @description
     * Gets if all necessary dependences are ready and therefore it is ok for this class to
     * register it's singleton instance with globalThis.ezApi.
     * @returns {boolean}
     * True if registration is pending and ezApi and EzEventEngine are ready; otherwise, false.
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzDateTime.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready;
    }

    /**
     * @static
     * @private
     * @readonly
     * @description
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     * True if the singleton instance is set and its registration state is REGISTERED; otherwise, false.
     */
    static get #ezIsRegistered() {
        return null != EzDateTime.ezInstance && EzRegistrationState.REGISTERED === EzDateTime.ezApiRegistrationState;
    }

    /**
     * @static
     * @private
     * @function
     * @description
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true if successful, false otherwise.
     * @returns {boolean}
     * True if the singleton instance is registered with globalThis.ezApi; otherwise, false.
     */
    static #ezRegistrator() {
        if (EzDateTime.#ezCanRegister && !EzDateTime.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzDateTime, EzDateTime.ezApiName);
        }

        return EzDateTime.#ezIsRegistered;
    }

    /**
     * @static
     * @description
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzDateTime.ezApiName
     *     2) Property getter EzDateTime.ezEventNames
     *     3) Property getter EzDateTime.ezInstance
     *     4) Property setter EzDateTime.ezInstance
     *     5) Property getter EzDateTime.ezApiRegistrationState
     *     6) Property setter EzDateTime.ezApiRegistrationState
     *     7) Property getter EzDateTime.#ezCanRegister
     *     8) Property getter EzDateTime.#ezIsRegistered
     *     9) Method EzDateTime.#ezRegistrator()
     */
    static {
        if (!EzDateTime.#ezIsRegistered) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzDateTime.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzDateTime.ezOnEzApiReadyEventName,
                    EzDateTime.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzDateTime.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! IMPORTANT: Do NOT EMTER ANY non-initialization or non-EzApi registration code after this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

export const ezDateTime = () => globalThis?.ezApi?.ezclocker?.ezDateTime;
